/**
 * Messages for one area of the app (document 15, section 5.1), English and Arabic side by side.
 * Every key lives under `accountingPages`; Arabic plurals have all six CLDR forms.
 */
import * as more2 from './accountingPages/more2.js';
import * as more from './accountingPages/more.js';

export const en = {
  accountingPages: {
    ...more2.en,
    ...more.en,
    journalSource: {
      manual: 'Journal entry',
      opening: 'Opening balance',
      invoice: 'Invoice',
      credit_note: 'Credit note',
      receipt: 'Payment',
      sales_receipt: 'Sales receipt',
      bill: 'Bill',
      expense: 'Expense',
      debit_note: 'Debit note',
      vendor_payment: 'Bill payment',
      transfer: 'Transfer',
      bank: 'Bank transaction',
      deposit: 'Deposit',
      reversal: 'Reversal',
      year_end: 'Year-end close',
      vat_return: 'VAT return',
      vat_payment: 'VAT payment',
      write_off: 'Write-off',
      depreciation: 'Depreciation',
      asset_disposal: 'Asset disposal',
      fx_realised: 'Exchange gain or loss',
      fx_reval: 'Currency revaluation',
      qbo_journal: 'QuickBooks transaction',
    },
    journalLines: {
      chooseAccount: 'Line {{n}}: choose an account.',
      enterAmount: 'Line {{n}}: enter a debit or a credit.',
      notBoth: 'Line {{n}}: enter a debit or a credit, not both.',
      chooseCustomer: 'Line {{n}}: choose the customer in Name for {{account}}.',
      chooseVendor: 'Line {{n}}: choose the vendor in Name for {{account}}.',
      twoLines: 'A journal entry needs at least two lines.',
      unbalanced: 'Debits and credits must be equal. Out of balance by {{amount}}.',
    },
    journalForm: {
      title: 'Journal entry',
      titleTemplate: 'Recurring journal entry',
      copyName: '{{name}} (copy)',
      unsavedConfirm: 'You have unsaved changes. Leave without saving?',
      postThroughDocuments: 'Post through Sales or Expenses',
      contactCustomer: 'Customer',
      contactVendor: 'Vendor',
      contactBoth: 'Customer and vendor',
      templateNameRequired: 'Enter a template name.',
      scheduleProblem: 'Schedule: {{problem}}.',
      dateRequired: 'Enter the journal date.',
      templateSaved: 'Recurring template "{{name}}" saved.',
      entrySaved: 'Journal entry no. {{no}} saved.',
      view: 'View',
      subtitleCopy: 'Copy of no. {{no}}',
      subtitleUse: 'From recurring template "{{name}}"',
      subtitleUseDue: 'From recurring template "{{name}}" · due {{date}}',
      subtitleTemplate: 'Template "{{name}}"',
      clear: 'Clear',
      makeRecurring: 'Make recurring',
      saveTemplate: 'Save template',
      staffOnly: 'Only your accounting team can post journal entries for this client.',
      journalDate: 'Journal date',
      booksClosed: 'The books are closed through {{date}}.',
      journalNo: 'Journal no.',
      assignedWhenSaved: 'Assigned when saved',
      isAdjusting: 'Is adjusting journal entry',
      total: 'Total',
      linesAria: 'Journal lines',
      colAccount: 'Account',
      colDebits: 'Debits ({{currency}})',
      colCredits: 'Credits ({{currency}})',
      colDescription: 'Description',
      colName: 'Name',
      colClass: 'Class',
      taxVat: 'VAT',
      taxGst: 'GST',
      accountLine: 'Account, line {{n}}',
      chooseAccount: 'Choose an account',
      debitsLine: 'Debits, line {{n}}',
      creditsLine: 'Credits, line {{n}}',
      descriptionLine: 'Description, line {{n}}',
      nameLine: 'Name, line {{n}}',
      taxLine: 'VAT, line {{n}}',
      classLine: 'Class, line {{n}}',
      deleteLine: 'Delete line {{n}}',
      outOfBalance: 'Out of balance by {{amount}}',
      balanced: 'Balanced',
      addLines: 'Add lines',
      clearAllLines: 'Clear all lines',
      tags: 'Tags',
      memo: 'Memo',
      shortcutsTemplate:
        'Shortcuts: Ctrl+Alt+S save and close · Ctrl+Alt+X close. Amount cells accept arithmetic such as 1200*1.05.',
      shortcutsEntry:
        'Shortcuts: Ctrl+Alt+S save and close · Ctrl+Alt+D save and new · Ctrl+Alt+X close. Amount cells accept arithmetic such as 1200*1.05. Date: T today, + / − next or previous day.',
    },
    journalList: {
      title: 'Journal entries',
      newEntry: 'New journal entry',
      loadError: 'Could not load journal entries',
      emptyTitle: 'No journal entries yet',
      emptyHint: 'Post the first one from + New › Journal entry.',
      colDate: 'Date',
      colNo: 'No.',
      colSource: 'Source',
      colMemo: 'Memo',
      colDebits: 'Debits',
      colCredits: 'Credits',
      colStatus: 'Status',
      colAction: 'Action',
      statusReversed: 'Reversed',
      statusAdjusting: 'Adjusting',
      statusPosted: 'Posted',
      view: 'View',
      moreActions: 'More actions for entry {{no}}',
      reverse: 'Reverse',
      copy: 'Copy',
      loadMore: 'Load more',
    },
    journalNew: {
      selectClientHint: 'Choose a client before adding entries.',
      templateError: 'Could not open the template',
    },
    journalDetail: {
      loadError: 'Could not open the journal entry',
      title: 'Journal entry no. {{no}}',
      more: 'More',
      lockedHint:
        'Dated on or before the lock date {{date}}; unlock the period to reverse it, or post a correcting entry.',
      wasReversed: 'This entry was reversed.',
      openReversing: 'Open the reversing entry',
      reversesOriginal: 'Reverses the original entry',
      reason: 'Reason:',
      journalDate: 'Journal date',
      journalNo: 'Journal no.',
      posted: 'Posted',
      total: 'Total',
      colAccount: 'Account',
      colDescription: 'Description',
      colName: 'Name',
      colTax: 'VAT',
      colClass: 'Class',
      colLocation: 'Location',
      memo: 'Memo',
    },
    reverseDialog: {
      title: 'Reverse journal entry no. {{no}}?',
      description:
        'A new entry with every debit and credit swapped is posted on the date below. The original stays in the books, marked reversed. This cannot be undone.',
      date: 'Reversal date',
      reason: 'Reason',
      reasonHint: 'At least 3 characters. Recorded in the audit log.',
      reversing: 'Reversing…',
      reverse: 'Reverse',
      reversed: 'Journal entry no. {{no}} reversed by no. {{reversal}}.',
      view: 'View',
    },
    openingBalance: {
      title: 'Opening balance',
      saved: 'Opening balance of {{name}} is {{amount}}.',
      removed: 'Opening balance of {{name}} removed.',
      dateRequired: 'Enter the date',
      descriptionCard:
        'What was owed on the card on the as-of date, from its statement. Posted against Opening balance equity. Changing it later replaces the entry.',
      descriptionBank:
        'The balance on your bank statement at the end of the as-of date. Posted against Opening balance equity. Changing it later replaces the entry.',
      amountHint: 'Leave empty or 0 to remove it.',
      asOf: 'As of',
      cardInCredit: 'The card was in credit (the card company owed you)',
      overdrawn: 'The account was overdrawn',
      current: 'Currently {{amount}} as of {{date}}',
      currentWithEntry: 'Currently {{amount}} as of {{date}} · Journal no. {{no}}',
    },
    register: {
      loadError: 'Could not load the register',
      back: 'Chart of accounts',
      endingBalance: 'Ending balance',
      from: 'From',
      to: 'To',
      clearDates: 'Clear dates',
      setOpeningBalance: 'Set opening balance',
      addJournalEntry: 'Add journal entry',
      empty: 'No transactions in this period',
      colDate: 'Date',
      colRefNo: 'Ref no.',
      colType: 'Type',
      colPayee: 'Payee',
      colAccount: 'Account',
      colMemo: 'Memo',
      deposit: 'Deposit',
      payment: 'Payment',
      increase: 'Increase',
      decrease: 'Decrease',
      balance: 'Balance',
      balanceIn: 'Balance ({{currency}})',
      amountIn: 'Amount ({{currency}})',
      openingBalance: 'Opening balance',
      reversed: 'Reversed',
    },
    periodsMonths: {
      m1: 'January',
      m2: 'February',
      m3: 'March',
      m4: 'April',
      m5: 'May',
      m6: 'June',
      m7: 'July',
      m8: 'August',
      m9: 'September',
      m10: 'October',
      m11: 'November',
      m12: 'December',
    },
    periodsPage: {
      statusOpen: 'Open',
      statusLocked: 'All months locked',
      statusClosed: 'Closed',
      yearAdded: 'Financial year {{label}} added.',
      selectClientHint: 'Choose a client to see its periods.',
      loadError: 'Could not load periods',
      title: 'Periods and close',
      description:
        'Lock the months you have finished so nothing dated in them can be posted, reversed or changed. Locking a month also locks every month before it.',
      addYear: 'Add financial year',
      booksClosedThrough: 'Books closed through {{date}}',
      lockedByOn:
        'Locked by {{name}} on {{date}}. Postings dated on or before this date are refused.',
      lockedBy: 'Locked by {{name}}. Postings dated on or before this date are refused.',
      anAccountant: 'an accountant',
      noneLocked: 'No months are locked yet.',
      needsRights: 'Locking and unlocking needs close rights for this client.',
      fyTitle: 'Financial year {{label}}',
      closedByOn: 'Closed by {{name}} on {{date}}',
      closedBy: 'Closed by {{name}}',
      closeEntryNo: 'close entry no. {{no}}',
      viewClose: 'View close',
      closeYear: 'Close year',
      monthLockedAria: '{{month}}, locked',
      monthOpenAria: '{{month}}, open',
      monthLockedByOn: 'Locked by {{name}} on {{date}}: {{reason}}',
      monthLockedBy: 'Locked by {{name}}: {{reason}}',
      locked: 'Locked',
      open: 'Open',
      noEntries: 'No entries',
      entries_one: '{{count}} entry',
      entries_other: '{{count}} entries',
      drafts_one: '{{count}} draft',
      drafts_other: '{{count}} drafts',
      toReview: '{{count}} to review',
      unlockFrom: 'Unlock from {{month}}',
      lockThrough: 'Lock through {{month}}',
      reopenFirst: 'Reopen the financial year first',
      lockTitle: 'Lock through {{month}}?',
      unlockTitle: 'Unlock from {{month}}?',
      lockDescription:
        'Nothing dated on or before {{date}} can be posted, reversed or changed once it is locked.',
      unlockDescription:
        'These months open for posting again. Lock them again when the changes are done.',
      months_one: 'Month:',
      months_other: '{{count}} months:',
      reason: 'Reason',
      reasonHint: 'Recorded with the period and in the audit log.',
      lockPlaceholder: 'For example: VAT return filed, month reviewed',
      unlockPlaceholder: 'Why it needs to open',
      warningsTitle: 'Some items in these months still need attention:',
      locking: 'Locking…',
      unlocking: 'Unlocking…',
      lockAnyway: 'Lock anyway',
      lock: 'Lock',
      unlock: 'Unlock',
      toastLocked: 'Books closed through {{date}}.',
      toastUnlocked: '{{range}} unlocked.',
    },
    periodClose: {
      stepCheck: 'Check the books',
      stepReview: 'Review the closing entry',
      stepClose: 'Close the year',
      loadError: 'Could not open the year-end close',
      title: 'Close financial year {{label}}',
      backToPeriods: 'Back to periods',
      back: 'Back',
      next: 'Next',
      stepsAria: 'Steps',
      iconDone: 'Done',
      iconWarning: 'Warning',
      iconBlocking: 'Must be fixed',
      checklistAria: 'Checklist',
      open: 'Open',
      review: 'Review',
      defaultLockReason: 'Year-end close {{label}}',
      toastLocked: 'Books closed through {{date}}.',
      reasonForLocking: 'Reason for locking',
      lockAnyway: 'Lock anyway',
      locking: 'Locking…',
      lockThrough: 'Lock through {{date}}',
      netLoss: 'Net loss for the year',
      netProfit: 'Net profit for the year',
      explainProfit:
        'A year-end close entry dated {{date}} brings every income and expense account to zero and moves the profit to {{account}}. Reports for this year still show its income and expenses.',
      explainLoss:
        'A year-end close entry dated {{date}} brings every income and expense account to zero and moves the loss to {{account}}. Reports for this year still show its income and expenses.',
      retainedEarnings: 'retained earnings',
      closingEntryAria: 'Closing entry',
      account: 'Account',
      debits: 'Debits',
      credits: 'Credits',
      total: 'Total',
      noClosingEntry: 'The year has no income or expenses, so no closing entry is needed.',
      toastClosed: 'Financial year {{label}} closed.',
      postEntry: 'Post the year-end close entry on {{date}} ({{amount}} to retained earnings).',
      markClosed: 'Mark financial year {{label}} closed. Its months stay locked.',
      nextExists: 'Financial year starting {{date}} is already set up.',
      addNext: 'Add the financial year {{from}} – {{to}}.',
      warnings: 'Warnings',
      reviewedWarnings: 'I have reviewed these warnings',
      fixFirst: 'Fix the items marked in the checklist first.',
      needsRights: 'Closing the year needs close rights for this client.',
      closing: 'Closing…',
      closedTitle: 'Financial year {{label}} is closed.',
      closedByOn: 'Closed by {{name}} on {{date}}.',
      closedBy: 'Closed by {{name}}.',
      anAccountant: 'an accountant',
      openCloseEntry: 'Open the close entry no. {{no}}',
      noProfitOrLoss: 'There was no profit or loss to move to retained earnings.',
      toastReopened: 'Financial year {{label}} reopened. Its months are still locked.',
      reopenYear: 'Reopen year',
      reopenTitle: 'Reopen financial year {{label}}?',
      reopenDescription:
        'The close entry is reversed on {{date}}. The months stay locked; unlock them on the Periods page if adjustments are needed.',
      reason: 'Reason',
      reasonHint: 'Recorded in the audit log.',
      reopening: 'Reopening…',
      reopen: 'Reopen',
    },
    reasonDialog: {
      reason: 'Reason',
      hint: 'Kept in the audit log.',
      working: 'Working…',
    },
    reclassifyPage: {
      types: {
        all: 'All transaction types',
        manual: 'Journal entry',
        invoice: 'Invoice',
        creditNote: 'Credit note',
        salesReceipt: 'Sales receipt',
        bill: 'Bill',
        debitNote: 'Debit note',
        expense: 'Expense',
        bank: 'Bank transaction',
        opening: 'Opening balance',
      },
      lineCount_one: '{{count}} line',
      lineCount_other: '{{count}} lines',
      transactionCount_one: '{{count}} transaction',
      transactionCount_other: '{{count}} transactions',
      toastDone: '{{lines}} reclassified in {{transactions}}.',
      view: 'View',
      noVatCodeLower: 'no VAT code',
      title: 'Reclassify transactions',
      description:
        'Move posted lines to another account or a VAT code without VAT. Each transaction is reversed and posted again on its own date; closed periods, filed VAT returns, reconciled lines and submitted e-invoices stay as they are.',
      rangeError: 'The end date is before the start date.',
      account: 'Account',
      allAccounts: 'All accounts',
      accountTypes: 'Account types',
      all: 'All',
      profitAndLoss: 'Profit and loss',
      balanceSheet: 'Balance sheet',
      from: 'From',
      to: 'To',
      name: 'Name',
      allNames: 'All names',
      transactionType: 'Transaction type',
      searchPlaceholder: 'Memo, number or name',
      search: 'Search',
      find: 'Find transactions',
      selected: '{{count}} selected',
      net: 'net {{amount}}',
      changeAccountTo: 'Change account to',
      keepAccount: 'Keep the account',
      changeVatTo: 'Change VAT code to',
      keepVat: 'Keep the VAT code',
      noVatCode: 'No VAT code',
      reclassify: 'Reclassify',
      clearSelection: 'Clear selection',
      loadError: 'Could not load transactions',
      noLines: 'No lines match',
      noLinesHint:
        'Change the account, dates or type. Bank, card, A/R, A/P and VAT lines are never listed here.',
      selectAll: 'Select all',
      colDate: 'Date',
      colType: 'Type',
      colNo: 'No.',
      colName: 'Name',
      colMemo: 'Memo/description',
      colAccount: 'Account',
      colVat: 'VAT',
      colAmount: 'Amount',
      latest300: 'Showing the latest 300 lines; narrow the filters to see more.',
      confirmTitle_one: 'Reclassify {{count}} line?',
      confirmTitle_other: 'Reclassify {{count}} lines?',
      moveTo: 'The lines move to {{account}}.',
      vatBecomes: 'Their VAT code becomes {{code}}.',
      confirmBody:
        'Each transaction affected is reversed and posted again on its own date with the change; on an invoice or bill, every line on the same account moves with it.',
      lockedAria: 'Locked: {{reason}}',
      selectLine: 'Select line {{line}} of {{document}}',
      journalNo: 'journal no. {{no}}',
    },
    writeOffPage: {
      overdueAny: 'Any open invoice',
      overdue: 'Overdue',
      overdue30: 'More than 30 days overdue',
      overdue60: 'More than 60 days overdue',
      overdue90: 'More than 90 days overdue',
      overdue180: 'More than 180 days overdue',
      title: 'Write off invoices',
      description:
        'Write the unpaid balance of invoices off to a bad-debt account. Each write-off settles its invoice and can be undone.',
      tabOpen: 'Invoices to write off',
      tabHistory: 'Written off',
      toastDone_one: '{{count}} invoice written off. See them under Written off.',
      toastDone_other: '{{count}} invoices written off. See them under Written off.',
      asOf: 'As of',
      show: 'Show',
      customer: 'Customer',
      allCustomers: 'All customers',
      loadError: 'Could not load invoices',
      none: 'No invoices to write off',
      noneHint: 'Nothing open matches these filters.',
      selectAll: 'Select all',
      colInvoiceNo: 'Invoice no.',
      colCustomer: 'Customer',
      colInvoiceDate: 'Invoice date',
      colDueDate: 'Due date',
      colDaysOverdue: 'Days overdue',
      colTotal: 'Total',
      colOpenBalance: 'Open balance',
      colVatInBalance: 'VAT in balance',
      colVatRelief: 'VAT relief',
      selectInvoice: 'Select {{number}}',
      claim: 'Claim',
      notEligible: 'Not eligible',
      writeOffDate: 'Write-off date',
      badDebtAccount: 'Bad-debt account',
      invoiceCount_one: '{{count}} invoice',
      invoiceCount_other: '{{count}} invoices',
      reliefClaimed: 'VAT relief claimed {{amount}}',
      writeOff: 'Write off',
      confirmTitle_one: 'Write off {{count}} invoice?',
      confirmTitle_other: 'Write off {{count}} invoices?',
      confirmBody:
        '{{amount}} goes to the bad-debt account; A/R is credited and each invoice is settled. You can undo a write-off later.',
      confirmBodyRelief:
        '{{amount}} goes to the bad-debt account and {{relief}} of VAT relief to VAT control, with an adjustment on the open VAT return; A/R is credited and each invoice is settled. You can undo a write-off later.',
      confirmNotified: 'Confirm the customer has been told before claiming VAT relief.',
      notified:
        'The customer has been notified that the amount is written off (needed for VAT relief)',
      toastUndone: 'Write-off of {{number}} undone; the invoice is open again.',
      historyLoadError: 'Could not load write-offs',
      noneYet: 'Nothing written off yet',
      noneYetHint: 'Write-offs you post appear here.',
      colDate: 'Date',
      colInvoice: 'Invoice',
      colAmount: 'Amount',
      colAccount: 'Account',
      colReason: 'Reason',
      colStatus: 'Status',
      colAction: 'Action',
      returnPeriod: 'Return {{period}}',
      posted: 'Posted',
      undone: 'Undone',
      undo: 'Undo',
      undoTitle: 'Undo the write-off of {{number}}?',
      undoDescription:
        'The write-off entry is reversed on its own date, the invoice is open again and any VAT relief adjustment is removed from the VAT return.',
      undoConfirm: 'Undo write-off',
    },
    accountantLib: {
      coverPage: 'Cover page',
      contents: 'Contents',
      notes: 'Notes',
      straightLine: 'Straight line',
      decliningBalance: 'Declining balance',
      statusDisposed: 'Disposed',
      statusFullyDepreciated: 'Fully depreciated',
      statusActive: 'Active',
      monthYear: '{{month}} {{year}}',
      lifeYearsMonths: '{{years}} {{months}}',
      months: {
        m1: 'Jan',
        m2: 'Feb',
        m3: 'Mar',
        m4: 'Apr',
        m5: 'May',
        m6: 'Jun',
        m7: 'Jul',
        m8: 'Aug',
        m9: 'Sep',
        m10: 'Oct',
        m11: 'Nov',
        m12: 'Dec',
      },
      lifeYears_one: '{{count}} year',
      lifeYears_other: '{{count}} years',
      lifeMonths_one: '{{count}} month',
      lifeMonths_other: '{{count}} months',
      errors: {
        upliftNotNumber: 'Enter a percentage such as 10 or -5.',
        upliftRange: 'Enter a change from -100% to 1000%.',
        packNameRequired: 'Name the management report.',
        packDatesRequired: 'Choose the dates, or pick a period.',
        packEndBeforeStart: 'The end date is before the start date.',
        packNoSections: 'Add at least one section.',
        packTooManySections: 'A pack can have at most {{max}} sections.',
        assetName: 'Name the asset',
        assetAccount: 'Choose the asset account',
        accumulatedAccount: 'Choose the accumulated depreciation account',
        expenseAccount: 'Choose the depreciation expense account',
        acquisitionDate: 'Enter the acquisition date',
        inServiceBeforeAcquired: 'The asset cannot go into service before it was acquired',
        cost: 'Enter the cost',
        residual: 'The residual value must be less than the cost',
        openingTooHigh: 'Opening depreciation cannot be more than cost less residual value',
        usefulLife: 'Enter the useful life',
        usefulLifeMax: 'The useful life can be at most 50 years',
        rate: 'Enter an annual rate from 0.01 to 100',
        openingThrough: 'Give the date the opening depreciation runs to',
        openingBeforeService: 'The opening figures end before the asset goes into service',
      },
    },
    assetDrawer: {
      chooseAccount: 'Choose an account',
      saved: 'Saved: {{name}}',
      newTitle: 'New fixed asset',
      lockedDescription:
        'Depreciation is booked, so cost, accounts, start and opening figures are fixed; useful life, residual value and method apply from the next month.',
      description:
        'What the asset cost, where it sits in the chart of accounts and how it depreciates.',
      name: 'Name',
      serialNo: 'Serial no.',
      vendor: 'Vendor',
      descriptionLabel: 'Description',
      assetAccount: 'Asset account',
      accumulatedAccount: 'Accumulated depreciation account',
      expenseAccount: 'Depreciation expense account',
      acquisitionDate: 'Acquisition date',
      inServiceFrom: 'In service from',
      inServiceHint: 'Depreciation starts this month (defaults to the acquisition date).',
      cost: 'Cost ({{currency}})',
      residual: 'Residual value ({{currency}})',
      method: 'Method',
      lifeYears: 'Useful life (years)',
      lifeMonths: 'and months',
      annualRate: 'Annual rate (%)',
      rateHint: 'Leave empty for double declining balance ({{rate}}% a year).',
      openingLegend: 'Depreciation booked before this register',
      openingAccumulated: 'Opening accumulated depreciation ({{currency}})',
      throughEndOf: 'Through the end of',
      throughHint: 'The month it covers.',
    },
    fixedAssetsList: {
      title: 'Fixed assets',
      description:
        'The asset register: cost, depreciation and net book value. Depreciation posts monthly through the ledger, and disposals book the gain or loss.',
      runDepreciation: 'Run depreciation',
      newAsset: 'New asset',
      loadError: 'Could not load the register',
      emptyTitle: 'No fixed assets yet',
      emptyHint: 'Add vehicles, equipment, fit-out and software with their cost and useful life.',
      colAssetNo: 'Asset no.',
      colName: 'Name',
      colAssetAccount: 'Asset account',
      colInService: 'In service',
      colMethod: 'Method',
      colCost: 'Cost',
      colAccumulated: 'Accumulated depreciation',
      colNbv: 'Net book value',
      colDepreciatedThrough: 'Depreciated through',
      colStatus: 'Status',
      methodLife: '{{method}}, {{life}}',
      totalInService: 'Total in service',
      runUndone: 'Depreciation for {{month}} undone.',
      runsTitle: 'Depreciation runs',
      colMonth: 'Month',
      colRun: 'Run',
      colAssets: 'Assets',
      colAmount: 'Amount',
      colJournalNo: 'Journal no.',
      colWhen: 'When',
      colAction: 'Action',
      triggerScheduled: 'Scheduled',
      triggerSetUp: 'Set up',
      triggerManual: 'Manual',
      statusPosted: 'Posted',
      statusUndone: 'Undone',
      undo: 'Undo',
      undoTitle: 'Undo depreciation for {{month}}?',
      undoDescription:
        "The run's entry is reversed on its own date and its months can be charged again. A closed month refuses it.",
      undoConfirm: 'Undo run',
      dialogDescription:
        'Posts one entry per month, dated its last day, for every asset with depreciation due. A month already charged is never charged again; the register also runs this every night.',
      throughEndOf: 'Through the end of',
      workingOut: 'Working out what is due…',
      nothingDue: 'Nothing is due through {{month}}.',
      closedMonthTitle: 'This month is closed; the run will stop here',
      closedMonth: 'Closed month',
      total: 'Total {{currency}} {{amount}}',
      posting: 'Posting…',
      postDepreciation: 'Post depreciation',
      postedAll_one: 'Depreciation posted for {{count}} month.',
      postedAll_other: 'Depreciation posted for {{count}} months.',
      postedPartial_one: '{{count}} month posted; {{month}} could not post: {{error}}',
      postedPartial_other: '{{count}} months posted; {{month}} could not post: {{error}}',
    },
    fixedAssetDetail: {
      scheduleOpening: 'Opening',
      scheduleAdopted: 'Linked entry',
      schedulePosted: 'Posted',
      schedulePlanned: 'Planned',
      backInRegister: '{{number}} is back in the register.',
      deleted: 'Asset deleted.',
      loadError: 'Could not load the asset',
      back: 'Fixed assets',
      rateAYear: '{{rate}}% a year',
      inService: 'in service {{date}}',
      serial: 'S/N {{serial}}',
      cost: 'Cost',
      accumulated: 'Accumulated depreciation',
      nbv: 'Net book value',
      linkEntries: 'Link existing entries ({{count}})',
      dispose: 'Dispose',
      undoDisposal: 'Undo disposal',
      more: 'More',
      deleteAsset: 'Delete asset',
      disposedOn: 'Disposed of on {{date}}',
      proceeds: 'Proceeds {{amount}}',
      proceedsInto: 'Proceeds {{amount}} into {{account}}',
      gainOn: 'Gain {{amount}} on {{account}}',
      lossOn: 'Loss {{amount}} on {{account}}',
      journalEntry: 'Journal entry',
      scheduleTitle: 'Depreciation schedule',
      colMonth: 'Month',
      colDepreciation: 'Depreciation',
      colAccumulated: 'Accumulated',
      colNbv: 'Net book value',
      colStatus: 'Status',
      throughMonth: 'Through {{month}}',
      undoDisposalTitle: 'Undo the disposal of {{number}}?',
      undoDisposalDescription:
        'The disposal entry is reversed on its own date and the asset goes back on the register.',
      deleteTitle: 'Delete {{number}}?',
      deleteDescription: 'Nothing is booked for this asset yet, so it is simply removed.',
      delete: 'Delete',
      disposalDateRequired: 'Enter the disposal date',
      disposalBeforeAcquired: 'The disposal date is before the asset was acquired',
      disposedToast: 'Disposed of: {{number}}',
      viewEntry: 'View entry',
      disposeTitle: 'Dispose of {{number}} {{name}}',
      disposeDescription:
        'Posts the cost and accumulated depreciation out, the proceeds in, and the gain or loss. Depreciation must be posted through the month before; none is charged in the month of disposal.',
      disposalDate: 'Disposal date',
      proceedsLabel: 'Proceeds ({{currency}})',
      proceedsHint: '0 when scrapped or written off.',
      proceedsPaidInto: 'Proceeds paid into',
      chooseAccount: 'Choose an account',
      gainLossAccount: 'Gain or loss account',
      note: 'Note',
      posting: 'Posting…',
      adoptTitle: 'Link existing depreciation entries',
      adoptDescription:
        'Journal entries that already credit {{account}} in this asset’s life. Linked months count as depreciated and are not posted again.',
      entryCheckbox: '{{date}} · no. {{number}}',
      selected: '{{amount}} selected',
      linkEntriesButton: 'Link entries',
      linkedToast_one: '{{count}} entry linked to {{number}}.',
      linkedToast_other: '{{count}} entries linked to {{number}}.',
    },
    managementReports: {
      standardName: 'Monthly management pack',
      copyName: '{{name}} (copy)',
      created: 'Created: {{name}}',
      deleted: 'Deleted: {{name}}',
      loadError: 'Could not load management reports',
      emptyTitle: 'No management reports yet',
      emptyHint:
        'A management report puts a cover, the reports you choose and your notes into one PDF for the board or the owner.',
      createStandard: 'Create the standard pack',
      new: 'New management report',
      colName: 'Name',
      colSections: 'Sections',
      colModified: 'Last modified',
      colAction: 'Action',
      exportPdf: 'Export PDF',
      moreActions: 'More actions for {{name}}',
      edit: 'Edit',
      copy: 'Copy',
      delete: 'Delete',
      noMatch: 'No management report matches.',
      deleteTitle: 'Delete “{{name}}”?',
      deleteDescription: 'The template is removed; the books and the reports are not affected.',
      exportTitle: 'Export “{{name}}”',
      exportDescription:
        'One PDF with the cover, contents, every report for the period and your notes.',
      reportPeriod: 'Report period',
      from: 'From',
      to: 'To',
      range: '{{from}} – {{to}}',
      asOf: 'As of {{date}}',
      preparing: 'Preparing PDF…',
      presets: {
        custom: 'Custom',
        this_week: 'This week',
        this_week_to_date: 'This week to date',
        this_month: 'This month',
        this_month_to_date: 'This month to date',
        this_quarter: 'This quarter',
        this_quarter_to_date: 'This quarter to date',
        this_year: 'This financial year',
        this_year_to_date: 'This financial year to date',
        yesterday: 'Yesterday',
        last_week: 'Last week',
        last_month: 'Last month',
        last_quarter: 'Last quarter',
        last_year: 'Last financial year',
        last_7_days: 'Last 7 days',
        last_30_days: 'Last 30 days',
      },
    },
    packEditor: {
      totalOnly: 'Total only',
      months: 'Months',
      quarters: 'Quarters',
      saved: 'Saved: {{name}}',
      back: 'Management reports',
      editTitle: 'Edit management report',
      newTitle: 'New management report',
      name: 'Name',
      reportPeriod: 'Report period',
      periodHint: 'Used when the pack is exported; you can change it then.',
      from: 'From',
      to: 'To',
      description: 'Description',
      sections: 'Sections',
      addSection: 'Add section',
      coverPage: 'Cover page',
      contents: 'Contents',
      notes: 'Notes',
      reports: 'Reports',
      comingSoon: 'Coming soon; left out of the PDF',
      moveUp: 'Move up',
      moveDown: 'Move down',
      remove: 'Remove {{name}}',
      coverTitle: 'Cover title',
      coverTitlePlaceholder: 'Title (the pack name)',
      coverSubtitle: 'Cover subtitle',
      subtitle: 'Subtitle',
      preparedBy: 'Prepared by',
      sectionTitle: 'Section title',
      title: 'Title',
      columnsBy: 'Display columns by',
      compare: 'Compare',
      noComparison: 'No comparison',
      previousPeriod: 'Previous period',
      previousYear: 'Previous year',
      notesTitle: 'Notes title',
      notesText: 'Notes text',
      notesPlaceholder: 'Commentary for the period: what moved, what to watch.',
      saveAndExport: 'Save and export',
    },
    recurringMode: {
      scheduled: 'Scheduled',
      reminder: 'Reminder',
      unscheduled: 'Unscheduled',
    },
    recurringKind: {
      journal_entry: 'Journal entry',
      invoice: 'Invoice',
      bill: 'Bill',
      expense: 'Expense',
    },
    recurringSchedule: {
      problem: {
        startDate: 'enter a start date',
        every: 'repeat every 1 to 999',
        dayOfWeek: 'choose a day of the week',
        dayOfMonth: 'choose a day of the month',
        month: 'choose a month',
        endAfter: 'end after 1 or more',
        endDate: 'the end date must be on or after the start date',
        daysWhole: 'days must be a whole number',
        daysMax: 'days in advance must be 365 or fewer',
        endAfterMax: 'end after 9999 occurrences or fewer',
      },
      every: {
        daily_one: 'Every day',
        daily_other: 'Every {{count}} days',
        weekly_one: 'Every week',
        weekly_other: 'Every {{count}} weeks',
        monthly_one: 'Every month',
        monthly_other: 'Every {{count}} months',
        yearly_one: 'Every year',
        yearly_other: 'Every {{count}} years',
      },
      weekly: '{{every}} on {{weekday}}',
      monthly: '{{every}} on day {{day}}',
      monthlyLast: '{{every}} on the last day',
      yearly: '{{every}} on {{day}} {{month}}',
      yearlyLast: '{{every}} on the last day of {{month}}',
      weekday: {
        d0: 'Sunday',
        d1: 'Monday',
        d2: 'Tuesday',
        d3: 'Wednesday',
        d4: 'Thursday',
        d5: 'Friday',
        d6: 'Saturday',
      },
      month: {
        m1: 'January',
        m2: 'February',
        m3: 'March',
        m4: 'April',
        m5: 'May',
        m6: 'June',
        m7: 'July',
        m8: 'August',
        m9: 'September',
        m10: 'October',
        m11: 'November',
        m12: 'December',
      },
    },
    recurringStrip: {
      ariaLabel: 'Recurring',
      templateName: 'Template name',
      type: 'Type',
      mode: 'Mode',
      createDaysInAdvance: 'Create days in advance',
      remindDaysBefore: 'Remind days before',
      unscheduledHint:
        'Unscheduled templates are not created automatically. Use them from the Recurring transactions list.',
      interval: 'Interval',
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly',
      yearly: 'Yearly',
      on: 'On',
      month: 'Month',
      day: 'Day',
      last: 'Last',
      everyDays: 'Every (days)',
      everyWeeks: 'Every (weeks)',
      everyMonths: 'Every (months)',
      everyYears: 'Every (years)',
      startDate: 'Start date',
      end: 'End',
      endNone: 'None',
      endAfter: 'After',
      endBy: 'By',
      occurrences: 'Occurrences',
      endDate: 'End date',
      firstOn: 'first on {{date}}',
      nextOn: 'next on {{date}}',
      pastDates: 'Dates before today are created on the next run.',
    },
    recurringList: {
      title: 'Recurring transactions',
      description:
        'Templates that create transactions on a schedule, remind you when they are due, or wait until you use them.',
      selectClientHint: 'Choose a client to see its templates.',
      paused: '"{{name}}" paused.',
      resumed: '"{{name}}" resumed.',
      resumedNext: '"{{name}}" resumed; next on {{date}}.',
      deleted: '"{{name}}" deleted.',
      draftCreated: {
        invoice: 'Draft invoice created from "{{name}}".',
        bill: 'Draft bill created from "{{name}}".',
        expense: 'Draft expense created from "{{name}}".',
      },
      newTemplate: 'New template',
      search: 'Search by template name',
      couldNotLoad: 'Could not load templates',
      emptyTitle: 'No recurring transactions yet',
      emptyHint:
        'Set up rent, retainers, subscriptions or accruals once and let them recur every period.',
      col: {
        templateName: 'Template name',
        type: 'Type',
        interval: 'Interval',
        previousDate: 'Previous date',
        nextDate: 'Next date',
        contact: 'Customer/Vendor',
        amount: 'Amount',
        mode: 'Mode',
        action: 'Action',
      },
      badgePaused: 'Paused',
      badgeDue: 'Due',
      createFailed: {
        journal_entry: 'Could not create the {{date}} entry: {{error}}',
        invoice: 'Could not create the {{date}} invoice: {{error}}',
        bill: 'Could not create the {{date}} bill: {{error}}',
        expense: 'Could not create the {{date}} expense: {{error}}',
      },
      createFailedOther: 'Could not create the {{date}} {{kind}}: {{error}}',
      use: 'Use',
      duplicate: 'Duplicate',
      resume: 'Resume',
      pause: 'Pause',
      delete: 'Delete',
      moreActions: 'More actions for {{name}}',
      deleteTitle: 'Delete “{{name}}”?',
      deleteDescription:
        'The template stops creating transactions. Transactions it already created stay in the books.',
      deleting: 'Deleting…',
    },
    recurringTemplate: {
      couldNotOpen: 'Could not open the template',
    },
    budgetList: {
      title: 'Budgets',
      description:
        'Plan income and expenses by account and month, then compare them with the actuals.',
      newBudget: 'New budget',
      deleted: 'Budget "{{name}}" deleted.',
      created: 'Budget "{{name}}" created.',
      couldNotLoad: 'Could not load budgets',
      emptyTitle: 'No budgets yet',
      emptyHint: "Start from last year's actuals with an uplift, or from a blank year.",
      col: {
        name: 'Name',
        financialYear: 'Financial year',
        income: 'Income',
        expenses: 'Expenses',
        netIncome: 'Net income',
        lastModified: 'Last modified',
        action: 'Action',
      },
      view: 'View',
      delete: 'Delete',
      moreActions: 'More actions for {{name}}',
      deleteTitle: 'Delete budget “{{name}}”?',
      deleteDescription: 'Its amounts are removed. The books and the actuals are not affected.',
      yearLabel: 'FY {{year}}',
      defaultName: 'Budget {{fy}}',
      dialogTitle: 'New budget',
      dialogDescription:
        'A budget covers one financial year, by profit and loss account and month.',
      name: 'Name',
      financialYear: 'Financial year',
      startFrom: 'Start from',
      fromActuals: 'Actuals of a financial year',
      fromBudget: 'Another budget',
      fromBlank: 'Blank',
      actualsOf: 'Actuals of',
      budget: 'Budget',
      chooseBudget: 'Choose a budget',
      changeBy: 'Change by (%)',
      changeByHint: '10 adds 10% to every amount; −5 takes 5% off.',
      creating: 'Creating…',
      create: 'Create budget',
    },
    budgetEditor: {
      couldNotLoad: 'Could not load the budget',
      budgetName: 'Budget name',
      nameRequired: 'Name the budget.',
      byMonth: '{{fy}} · by month',
      showEmpty: 'Show accounts without amounts',
      vsActuals: 'Budget vs actuals',
      revert: 'Revert',
      saved: 'Budget "{{name}}" saved.',
      account: 'Account',
      total: 'Total',
      rowActions: 'Row actions for {{name}}',
      copyAcross: 'Copy first month across',
      spread: 'Spread total evenly',
      clearRow: 'Clear row',
      sectionTotal: 'Total {{section}}',
      netIncome: 'Net income',
      section: {
        income: 'Income',
        cost_of_sales: 'Cost of sales',
        expenses: 'Expenses',
        other_income: 'Other income',
        other_expense: 'Other expenses',
      },
    },
    practiceClients: {
      title: 'Clients',
      description: 'Every client whose books you can open.',
      addClient: 'Add client',
      search: 'Search by name or tax number',
      searchLabel: 'Search clients',
      couldNotLoad: 'Could not load clients',
      emptyTitle: 'No clients yet',
      noMatch: 'No clients match your search',
      emptyHint: 'Add the first client to start keeping its books.',
      col: {
        client: 'Client',
        taxNumber: 'Tax number',
        taxPeriod: 'Tax period',
        bankItems: 'Bank items to review',
        unreconciled: 'Unreconciled accounts',
        lastLocked: 'Last locked period',
        accountants: 'Accountants',
        action: 'Action',
      },
      open: 'Open',
      openBooks: 'Open books',
      statusError: 'Could not read this client’s status',
      notRegisteredVat: 'Not registered for VAT',
      notRegisteredGst: 'Not registered for GST',
      due: '{{label}} · due {{date}}',
      filed: {
        monthly: 'monthly · Filed',
        quarterly: 'quarterly · Filed',
      },
      filedOther: '{{period}} · Filed',
      chip: {
        open: 'Open',
        dueToday: 'Due today',
        overdue: 'Overdue',
        filed: 'Filed',
        paid: 'Paid',
        dueIn_one: 'Due in {{count}} day',
        dueIn_other: 'Due in {{count}} days',
      },
      added: '{{name}} added.',
      drawerTitle: 'Add client',
      drawerDescription:
        "Sets up the books: financial years, tax rates for the client's country and a chart of accounts from the template.",
      country: 'Country',
      countryHint: 'Sets the home currency, tax, financial year and tax number.',
      india: 'India',
      uae: 'United Arab Emirates',
      clientName: 'Client name',
      legalName: 'Legal name',
      legalNameHintIndia:
        'As registered (certificate of incorporation or GST registration). Defaults to the client name.',
      legalNameHintUae: 'As registered (trade licence). Defaults to the client name.',
      state: 'State',
      emirate: 'Emirate',
      gstin: 'GSTIN',
      gstinHint: '15 characters; leave blank if not registered for GST.',
      trn: 'TRN',
      trnHint: '15 digits; leave blank if not VAT registered.',
      homeCurrency: 'Home currency',
      currency: {
        IN: 'INR – Indian rupee',
        AE: 'AED – UAE dirham',
      },
      firstMonth: 'First month of financial year',
      returnPeriodVat: 'VAT return period',
      returnPeriodGst: 'GST return period',
      quarterly: 'Quarterly',
      monthly: 'Monthly',
      chartTemplate: 'Chart of accounts template',
      template: {
        trading: 'Trading (goods)',
        services: 'Services',
        free_zone: 'Free zone company',
        india_trading: 'Trading (goods), India',
        india_services: 'Services, India',
      },
      error: {
        name: 'Enter the client name.',
        trn: 'A TRN has 15 digits.',
        gstinState: 'The GSTIN is registered in {{state}}.',
      },
      emirates: {
        abuDhabi: 'Abu Dhabi',
        dubai: 'Dubai',
        sharjah: 'Sharjah',
        ajman: 'Ajman',
        ummAlQuwain: 'Umm Al Quwain',
        rasAlKhaimah: 'Ras Al Khaimah',
        fujairah: 'Fujairah',
      },
      countOf_one: '{{shown}} of {{count}} clients',
      countOf_other: '{{shown}} of {{count}} clients',
      overdue_one: '{{count}} overdue',
      overdue_other: '{{count}} overdue',
    },
    shortcuts: {
      title: 'Keyboard shortcuts',
      description: 'On macOS use Control+Option.',
      anywhere: 'Anywhere',
      dateFields: 'Date fields',
      amountFields: 'Amount fields',
      comingSoon: 'Coming soon',
      amountHint:
        'Type arithmetic such as <c1>1200*1.05</c1> or <c2>(450+380)/2</c2>; it is worked out when you leave the field or press <k>=</k>.',
      action: {
        chartOfAccounts: 'Chart of accounts',
        search: 'Search',
        searchSlash: 'Search (when not typing in a field)',
        showShortcuts: 'Show shortcuts',
        saveClose: 'Save and close (in a form)',
        saveNew: 'Save and new (in a form)',
        exitForm: 'Exit the form (inside a form)',
        newInvoice: 'New invoice',
        newQuote: 'New quote',
        receivePayment: 'Receive payment',
        vendorPayment: 'Vendor payment',
        customers: 'Customers list',
        vendors: 'Vendors list',
        lists: 'Lists (settings gear)',
        help: 'Help',
        send: 'Send, or approve and send (in a form)',
      },
      date: {
        today: 'Today',
        nextPrevDay: 'Next / previous day',
        week: 'First / last day of the week',
        month: 'First / last day of the month',
        year: 'First / last day of the year',
      },
    },
  },
};

export const ar = {
  accountingPages: {
    ...more2.ar,
    ...more.ar,
    journalSource: {
      manual: 'قيد يومية',
      opening: 'رصيد افتتاحي',
      invoice: 'فاتورة',
      credit_note: 'إشعار دائن',
      receipt: 'سند قبض',
      sales_receipt: 'إيصال مبيعات',
      bill: 'فاتورة مورد',
      expense: 'مصروف',
      debit_note: 'إشعار مدين',
      vendor_payment: 'سداد فاتورة مورد',
      transfer: 'تحويل',
      bank: 'معاملة بنكية',
      deposit: 'إيداع',
      reversal: 'قيد عكسي',
      year_end: 'إقفال نهاية السنة',
      vat_return: 'إقرار ضريبة القيمة المضافة',
      vat_payment: 'سداد ضريبة القيمة المضافة',
      write_off: 'شطب',
      depreciation: 'إهلاك',
      asset_disposal: 'استبعاد أصل',
      fx_realised: 'أرباح أو خسائر صرف العملات',
      fx_reval: 'إعادة تقييم العملات',
      qbo_journal: 'معاملة QuickBooks',
    },
    journalLines: {
      chooseAccount: 'السطر {{n}}: اختر حساباً.',
      enterAmount: 'السطر {{n}}: أدخل مبلغاً مديناً أو دائناً.',
      notBoth: 'السطر {{n}}: أدخل مبلغاً مديناً أو دائناً، وليس كليهما.',
      chooseCustomer: 'السطر {{n}}: اختر العميل في حقل الاسم للحساب {{account}}.',
      chooseVendor: 'السطر {{n}}: اختر المورد في حقل الاسم للحساب {{account}}.',
      twoLines: 'يحتاج قيد اليومية إلى سطرين على الأقل.',
      unbalanced: 'يجب أن يتساوى المدين والدائن. الفرق {{amount}}.',
    },
    journalForm: {
      title: 'قيد يومية',
      titleTemplate: 'قيد يومية متكرر',
      copyName: '{{name}} (نسخة)',
      unsavedConfirm: 'لديك تغييرات غير محفوظة. هل تريد المغادرة دون حفظ؟',
      postThroughDocuments: 'يُرحَّل من خلال المبيعات أو المصروفات',
      contactCustomer: 'عميل',
      contactVendor: 'مورد',
      contactBoth: 'عميل ومورد',
      templateNameRequired: 'أدخل اسم النموذج.',
      scheduleProblem: 'الجدولة: {{problem}}.',
      dateRequired: 'أدخل تاريخ القيد.',
      templateSaved: 'تم حفظ النموذج المتكرر "{{name}}".',
      entrySaved: 'تم حفظ قيد اليومية رقم {{no}}.',
      view: 'عرض',
      subtitleCopy: 'نسخة من القيد رقم {{no}}',
      subtitleUse: 'من النموذج المتكرر "{{name}}"',
      subtitleUseDue: 'من النموذج المتكرر "{{name}}" · مستحق في {{date}}',
      subtitleTemplate: 'النموذج "{{name}}"',
      clear: 'مسح',
      makeRecurring: 'جعله متكرراً',
      saveTemplate: 'حفظ النموذج',
      staffOnly: 'يقتصر ترحيل قيود اليومية لهذا العميل على فريق المحاسبة الخاص بك.',
      journalDate: 'تاريخ القيد',
      booksClosed: 'الدفاتر مقفلة حتى {{date}}.',
      journalNo: 'رقم القيد',
      assignedWhenSaved: 'يُحدَّد عند الحفظ',
      isAdjusting: 'قيد تسوية',
      total: 'الإجمالي',
      linesAria: 'سطور القيد',
      colAccount: 'الحساب',
      colDebits: 'مدين ({{currency}})',
      colCredits: 'دائن ({{currency}})',
      colDescription: 'الوصف',
      colName: 'الاسم',
      colClass: 'الفئة',
      taxVat: 'الضريبة',
      taxGst: 'ضريبة السلع والخدمات',
      accountLine: 'الحساب، السطر {{n}}',
      chooseAccount: 'اختر حساباً',
      debitsLine: 'مدين، السطر {{n}}',
      creditsLine: 'دائن، السطر {{n}}',
      descriptionLine: 'الوصف، السطر {{n}}',
      nameLine: 'الاسم، السطر {{n}}',
      taxLine: 'الضريبة، السطر {{n}}',
      classLine: 'الفئة، السطر {{n}}',
      deleteLine: 'حذف السطر {{n}}',
      outOfBalance: 'غير متوازن بفرق {{amount}}',
      balanced: 'متوازن',
      addLines: 'إضافة سطور',
      clearAllLines: 'مسح جميع السطور',
      tags: 'الوسوم',
      memo: 'البيان',
      shortcutsTemplate:
        'اختصارات: Ctrl+Alt+S حفظ وإغلاق · Ctrl+Alt+X إغلاق. تقبل خانات المبالغ العمليات الحسابية مثل 1200*1.05.',
      shortcutsEntry:
        'اختصارات: Ctrl+Alt+S حفظ وإغلاق · Ctrl+Alt+D حفظ وجديد · Ctrl+Alt+X إغلاق. تقبل خانات المبالغ العمليات الحسابية مثل 1200*1.05. التاريخ: T لليوم، و+ / − لليوم التالي أو السابق.',
    },
    journalList: {
      title: 'قيود اليومية',
      newEntry: 'قيد يومية جديد',
      loadError: 'تعذّر تحميل قيود اليومية',
      emptyTitle: 'لا توجد قيود يومية بعد',
      emptyHint: 'رحّل أول قيد من + جديد › قيد يومية.',
      colDate: 'التاريخ',
      colNo: 'الرقم',
      colSource: 'المصدر',
      colMemo: 'البيان',
      colDebits: 'مدين',
      colCredits: 'دائن',
      colStatus: 'الحالة',
      colAction: 'إجراء',
      statusReversed: 'معكوس',
      statusAdjusting: 'تسوية',
      statusPosted: 'مُرحَّل',
      view: 'عرض',
      moreActions: 'إجراءات أخرى للقيد {{no}}',
      reverse: 'عكس القيد',
      copy: 'نسخ',
      loadMore: 'تحميل المزيد',
    },
    journalNew: {
      selectClientHint: 'اختر عميلاً قبل إضافة القيود.',
      templateError: 'تعذّر فتح النموذج',
    },
    journalDetail: {
      loadError: 'تعذّر فتح قيد اليومية',
      title: 'قيد يومية رقم {{no}}',
      more: 'المزيد',
      lockedHint:
        'مؤرخ في تاريخ الإقفال {{date}} أو قبله؛ افتح الفترة لعكسه، أو رحّل قيداً تصحيحياً.',
      wasReversed: 'تم عكس هذا القيد.',
      openReversing: 'فتح القيد العكسي',
      reversesOriginal: 'يعكس القيد الأصلي',
      reason: 'السبب:',
      journalDate: 'تاريخ القيد',
      journalNo: 'رقم القيد',
      posted: 'تاريخ الترحيل',
      total: 'الإجمالي',
      colAccount: 'الحساب',
      colDescription: 'الوصف',
      colName: 'الاسم',
      colTax: 'الضريبة',
      colClass: 'الفئة',
      colLocation: 'الموقع',
      memo: 'البيان',
    },
    reverseDialog: {
      title: 'عكس قيد اليومية رقم {{no}}؟',
      description:
        'سيُرحَّل قيد جديد تُبدَّل فيه جميع المبالغ المدينة والدائنة في التاريخ أدناه. يبقى القيد الأصلي في الدفاتر مع وسمه بأنه معكوس. لا يمكن التراجع عن ذلك.',
      date: 'تاريخ العكس',
      reason: 'السبب',
      reasonHint: '3 أحرف على الأقل. يُسجَّل في سجل التدقيق.',
      reversing: 'جارٍ العكس…',
      reverse: 'عكس القيد',
      reversed: 'تم عكس قيد اليومية رقم {{no}} بالقيد رقم {{reversal}}.',
      view: 'عرض',
    },
    openingBalance: {
      title: 'الرصيد الافتتاحي',
      saved: 'الرصيد الافتتاحي لحساب {{name}} هو {{amount}}.',
      removed: 'تمت إزالة الرصيد الافتتاحي لحساب {{name}}.',
      dateRequired: 'أدخل التاريخ',
      descriptionCard:
        'المبلغ المستحق على البطاقة في تاريخ الرصيد، من كشفها. يُرحَّل مقابل رصيد افتتاحي – حقوق الملكية. تغييره لاحقاً يستبدل القيد.',
      descriptionBank:
        'الرصيد في كشف حسابك البنكي في نهاية تاريخ الرصيد. يُرحَّل مقابل رصيد افتتاحي – حقوق الملكية. تغييره لاحقاً يستبدل القيد.',
      amountHint: 'اتركه فارغاً أو 0 لإزالته.',
      asOf: 'في تاريخ',
      cardInCredit: 'كان رصيد البطاقة دائناً (شركة البطاقة مدينة لك)',
      overdrawn: 'كان الحساب مكشوفاً',
      current: 'الرصيد الحالي {{amount}} في {{date}}',
      currentWithEntry: 'الرصيد الحالي {{amount}} في {{date}} · قيد رقم {{no}}',
    },
    register: {
      loadError: 'تعذّر تحميل سجل الحساب',
      back: 'دليل الحسابات',
      endingBalance: 'الرصيد الختامي',
      from: 'من',
      to: 'إلى',
      clearDates: 'مسح التواريخ',
      setOpeningBalance: 'تحديد الرصيد الافتتاحي',
      addJournalEntry: 'إضافة قيد يومية',
      empty: 'لا توجد معاملات في هذه الفترة',
      colDate: 'التاريخ',
      colRefNo: 'رقم المرجع',
      colType: 'النوع',
      colPayee: 'المستفيد',
      colAccount: 'الحساب',
      colMemo: 'البيان',
      deposit: 'إيداع',
      payment: 'دفعة',
      increase: 'زيادة',
      decrease: 'نقص',
      balance: 'الرصيد',
      balanceIn: 'الرصيد ({{currency}})',
      amountIn: 'المبلغ ({{currency}})',
      openingBalance: 'الرصيد الافتتاحي',
      reversed: 'معكوس',
    },
    periodsMonths: {
      m1: 'يناير',
      m2: 'فبراير',
      m3: 'مارس',
      m4: 'أبريل',
      m5: 'مايو',
      m6: 'يونيو',
      m7: 'يوليو',
      m8: 'أغسطس',
      m9: 'سبتمبر',
      m10: 'أكتوبر',
      m11: 'نوفمبر',
      m12: 'ديسمبر',
    },
    periodsPage: {
      statusOpen: 'مفتوحة',
      statusLocked: 'جميع الأشهر مقفلة',
      statusClosed: 'مُقفلة نهائياً',
      yearAdded: 'تمت إضافة السنة المالية {{label}}.',
      selectClientHint: 'اختر شركة لعرض فتراتها.',
      loadError: 'تعذّر تحميل الفترات',
      title: 'الفترات والإقفال',
      description:
        'أقفل الأشهر التي انتهيت منها حتى لا يمكن ترحيل أو عكس أو تعديل أي شيء مؤرخ فيها. إقفال شهر يُقفل أيضاً جميع الأشهر التي قبله.',
      addYear: 'إضافة سنة مالية',
      booksClosedThrough: 'الدفاتر مقفلة حتى {{date}}',
      lockedByOn: 'أقفلها {{name}} في {{date}}. تُرفض القيود المؤرخة في هذا التاريخ أو قبله.',
      lockedBy: 'أقفلها {{name}}. تُرفض القيود المؤرخة في هذا التاريخ أو قبله.',
      anAccountant: 'أحد المحاسبين',
      noneLocked: 'لم يُقفل أي شهر بعد.',
      needsRights: 'يتطلب الإقفال وإلغاؤه صلاحية الإقفال لهذه الشركة.',
      fyTitle: 'السنة المالية {{label}}',
      closedByOn: 'أقفلها {{name}} في {{date}}',
      closedBy: 'أقفلها {{name}}',
      closeEntryNo: 'قيد الإقفال رقم {{no}}',
      viewClose: 'عرض الإقفال',
      closeYear: 'إقفال السنة',
      monthLockedAria: '{{month}}، مقفل',
      monthOpenAria: '{{month}}، مفتوح',
      monthLockedByOn: 'أقفله {{name}} في {{date}}: {{reason}}',
      monthLockedBy: 'أقفله {{name}}: {{reason}}',
      locked: 'مقفل',
      open: 'مفتوح',
      noEntries: 'لا توجد قيود',
      entries_zero: 'لا توجد قيود',
      entries_one: 'قيد واحد',
      entries_two: 'قيدان',
      entries_few: '{{count}} قيود',
      entries_many: '{{count}} قيداً',
      entries_other: '{{count}} قيد',
      drafts_zero: 'لا توجد مسودات',
      drafts_one: 'مسودة واحدة',
      drafts_two: 'مسودتان',
      drafts_few: '{{count}} مسودات',
      drafts_many: '{{count}} مسودةً',
      drafts_other: '{{count}} مسودة',
      toReview: 'للمراجعة: {{count}}',
      unlockFrom: 'إلغاء الإقفال من {{month}}',
      lockThrough: 'إقفال حتى {{month}}',
      reopenFirst: 'أعد فتح السنة المالية أولاً',
      lockTitle: 'إقفال حتى {{month}}؟',
      unlockTitle: 'إلغاء الإقفال من {{month}}؟',
      lockDescription: 'بعد الإقفال لا يمكن ترحيل أو عكس أو تعديل أي شيء مؤرخ في {{date}} أو قبله.',
      unlockDescription:
        'تُفتح هذه الأشهر للترحيل مجدداً. أقفلها مرة أخرى عند الانتهاء من التعديلات.',
      months_zero: 'لا أشهر:',
      months_one: 'شهر واحد:',
      months_two: 'شهران:',
      months_few: '{{count}} أشهر:',
      months_many: '{{count}} شهراً:',
      months_other: '{{count}} شهر:',
      reason: 'السبب',
      reasonHint: 'يُسجَّل مع الفترة وفي سجل التدقيق.',
      lockPlaceholder: 'مثال: تم تقديم إقرار ضريبة القيمة المضافة، تمت مراجعة الشهر',
      unlockPlaceholder: 'سبب الحاجة إلى الفتح',
      warningsTitle: 'لا تزال بعض البنود في هذه الأشهر بحاجة إلى معالجة:',
      locking: 'جارٍ الإقفال…',
      unlocking: 'جارٍ إلغاء الإقفال…',
      lockAnyway: 'الإقفال رغم ذلك',
      lock: 'إقفال',
      unlock: 'إلغاء الإقفال',
      toastLocked: 'الدفاتر مقفلة حتى {{date}}.',
      toastUnlocked: 'تم إلغاء إقفال {{range}}.',
    },
    periodClose: {
      stepCheck: 'مراجعة الدفاتر',
      stepReview: 'مراجعة قيد الإقفال',
      stepClose: 'إقفال السنة',
      loadError: 'تعذّر فتح إقفال نهاية السنة',
      title: 'إقفال السنة المالية {{label}}',
      backToPeriods: 'العودة إلى الفترات',
      back: 'رجوع',
      next: 'التالي',
      stepsAria: 'الخطوات',
      iconDone: 'تم',
      iconWarning: 'تحذير',
      iconBlocking: 'يجب إصلاحه',
      checklistAria: 'قائمة التحقق',
      open: 'فتح',
      review: 'مراجعة',
      defaultLockReason: 'إقفال نهاية السنة {{label}}',
      toastLocked: 'الدفاتر مقفلة حتى {{date}}.',
      reasonForLocking: 'سبب الإقفال',
      lockAnyway: 'الإقفال رغم ذلك',
      locking: 'جارٍ الإقفال…',
      lockThrough: 'إقفال حتى {{date}}',
      netLoss: 'صافي خسارة السنة',
      netProfit: 'صافي ربح السنة',
      explainProfit:
        'قيد إقفال نهاية السنة بتاريخ {{date}} يُصفّر جميع حسابات الإيرادات والمصروفات وينقل الربح إلى {{account}}. تظل تقارير هذه السنة تعرض إيراداتها ومصروفاتها.',
      explainLoss:
        'قيد إقفال نهاية السنة بتاريخ {{date}} يُصفّر جميع حسابات الإيرادات والمصروفات وينقل الخسارة إلى {{account}}. تظل تقارير هذه السنة تعرض إيراداتها ومصروفاتها.',
      retainedEarnings: 'الأرباح المحتجزة',
      closingEntryAria: 'قيد الإقفال',
      account: 'الحساب',
      debits: 'مدين',
      credits: 'دائن',
      total: 'الإجمالي',
      noClosingEntry: 'لا توجد إيرادات أو مصروفات في هذه السنة، لذلك لا حاجة إلى قيد إقفال.',
      toastClosed: 'تم إقفال السنة المالية {{label}}.',
      postEntry:
        'ترحيل قيد إقفال نهاية السنة بتاريخ {{date}} (نقل {{amount}} إلى الأرباح المحتجزة).',
      markClosed: 'اعتبار السنة المالية {{label}} مُقفلة. تبقى أشهرها مقفلة.',
      nextExists: 'السنة المالية التي تبدأ في {{date}} مُعدّة مسبقاً.',
      addNext: 'إضافة السنة المالية {{from}} – {{to}}.',
      warnings: 'تحذيرات',
      reviewedWarnings: 'راجعتُ هذه التحذيرات',
      fixFirst: 'عالج البنود المحددة في قائمة التحقق أولاً.',
      needsRights: 'يتطلب إقفال السنة صلاحية الإقفال لهذه الشركة.',
      closing: 'جارٍ الإقفال…',
      closedTitle: 'السنة المالية {{label}} مُقفلة.',
      closedByOn: 'أقفلها {{name}} في {{date}}.',
      closedBy: 'أقفلها {{name}}.',
      anAccountant: 'أحد المحاسبين',
      openCloseEntry: 'فتح قيد الإقفال رقم {{no}}',
      noProfitOrLoss: 'لم يكن هناك ربح أو خسارة لنقلها إلى الأرباح المحتجزة.',
      toastReopened: 'أُعيد فتح السنة المالية {{label}}. لا تزال أشهرها مقفلة.',
      reopenYear: 'إعادة فتح السنة',
      reopenTitle: 'إعادة فتح السنة المالية {{label}}؟',
      reopenDescription:
        'يُعكس قيد الإقفال بتاريخ {{date}}. تبقى الأشهر مقفلة؛ ألغِ إقفالها من صفحة الفترات إذا لزمت تسويات.',
      reason: 'السبب',
      reasonHint: 'يُسجَّل في سجل التدقيق.',
      reopening: 'جارٍ إعادة الفتح…',
      reopen: 'إعادة الفتح',
    },
    reasonDialog: {
      reason: 'السبب',
      hint: 'يُحفظ في سجل التدقيق.',
      working: 'جارٍ التنفيذ…',
    },
    reclassifyPage: {
      types: {
        all: 'جميع أنواع المعاملات',
        manual: 'قيد يومية',
        invoice: 'فاتورة',
        creditNote: 'إشعار دائن',
        salesReceipt: 'إيصال مبيعات',
        bill: 'فاتورة مورد',
        debitNote: 'إشعار مدين',
        expense: 'مصروف',
        bank: 'معاملة بنكية',
        opening: 'رصيد افتتاحي',
      },
      lineCount_zero: '0 سطر',
      lineCount_one: 'سطر واحد',
      lineCount_two: 'سطران',
      lineCount_few: '{{count}} أسطر',
      lineCount_many: '{{count}} سطراً',
      lineCount_other: '{{count}} سطر',
      transactionCount_zero: '0 معاملة',
      transactionCount_one: 'معاملة واحدة',
      transactionCount_two: 'معاملتين',
      transactionCount_few: '{{count}} معاملات',
      transactionCount_many: '{{count}} معاملةً',
      transactionCount_other: '{{count}} معاملة',
      toastDone: 'تمت إعادة تصنيف {{lines}} في {{transactions}}.',
      view: 'عرض',
      noVatCodeLower: 'بدون رمز ضريبة',
      title: 'إعادة تصنيف المعاملات',
      description:
        'انقل الأسطر المرحّلة إلى حساب آخر أو إلى رمز ضريبة بلا ضريبة. تُعكس كل معاملة وتُرحَّل من جديد بتاريخها؛ وتبقى الفترات المقفلة وإقرارات ضريبة القيمة المضافة المقدَّمة والأسطر المسوّاة والفواتير الإلكترونية المُرسلة كما هي.',
      rangeError: 'تاريخ النهاية قبل تاريخ البداية.',
      account: 'الحساب',
      allAccounts: 'جميع الحسابات',
      accountTypes: 'أنواع الحسابات',
      all: 'الكل',
      profitAndLoss: 'الأرباح والخسائر',
      balanceSheet: 'الميزانية العمومية',
      from: 'من',
      to: 'إلى',
      name: 'الاسم',
      allNames: 'جميع الأسماء',
      transactionType: 'نوع المعاملة',
      searchPlaceholder: 'البيان أو الرقم أو الاسم',
      search: 'بحث',
      find: 'البحث عن المعاملات',
      selected: 'المحدد: {{count}}',
      net: 'الصافي {{amount}}',
      changeAccountTo: 'تغيير الحساب إلى',
      keepAccount: 'الإبقاء على الحساب',
      changeVatTo: 'تغيير رمز الضريبة إلى',
      keepVat: 'الإبقاء على رمز الضريبة',
      noVatCode: 'بدون رمز ضريبة',
      reclassify: 'إعادة تصنيف',
      clearSelection: 'مسح التحديد',
      loadError: 'تعذّر تحميل المعاملات',
      noLines: 'لا توجد أسطر مطابقة',
      noLinesHint:
        'غيّر الحساب أو التواريخ أو النوع. لا تظهر هنا أبداً أسطر البنوك والبطاقات والذمم المدينة والذمم الدائنة وضريبة القيمة المضافة.',
      selectAll: 'تحديد الكل',
      colDate: 'التاريخ',
      colType: 'النوع',
      colNo: 'الرقم',
      colName: 'الاسم',
      colMemo: 'البيان/الوصف',
      colAccount: 'الحساب',
      colVat: 'الضريبة',
      colAmount: 'المبلغ',
      latest300: 'تُعرض أحدث 300 سطر؛ ضيّق عوامل التصفية لعرض المزيد.',
      confirmTitle_zero: 'إعادة تصنيف 0 سطر؟',
      confirmTitle_one: 'إعادة تصنيف سطر واحد؟',
      confirmTitle_two: 'إعادة تصنيف سطرين؟',
      confirmTitle_few: 'إعادة تصنيف {{count}} أسطر؟',
      confirmTitle_many: 'إعادة تصنيف {{count}} سطراً؟',
      confirmTitle_other: 'إعادة تصنيف {{count}} سطر؟',
      moveTo: 'تنتقل الأسطر إلى {{account}}.',
      vatBecomes: 'يصبح رمز الضريبة لها {{code}}.',
      confirmBody:
        'تُعكس كل معاملة متأثرة وتُرحَّل من جديد بتاريخها مع التغيير؛ وفي الفاتورة أو فاتورة المورد تنتقل معها جميع الأسطر على الحساب نفسه.',
      lockedAria: 'مقفل: {{reason}}',
      selectLine: 'تحديد السطر {{line}} من {{document}}',
      journalNo: 'القيد رقم {{no}}',
    },
    writeOffPage: {
      overdueAny: 'أي فاتورة مفتوحة',
      overdue: 'متأخرة السداد',
      overdue30: 'متأخرة أكثر من 30 يوماً',
      overdue60: 'متأخرة أكثر من 60 يوماً',
      overdue90: 'متأخرة أكثر من 90 يوماً',
      overdue180: 'متأخرة أكثر من 180 يوماً',
      title: 'شطب الفواتير',
      description:
        'اشطب الرصيد غير المدفوع للفواتير إلى حساب الديون المعدومة. كل عملية شطب تسوّي فاتورتها ويمكن التراجع عنها.',
      tabOpen: 'فواتير للشطب',
      tabHistory: 'المشطوبة',
      toastDone_zero: 'لم تُشطب أي فاتورة.',
      toastDone_one: 'تم شطب فاتورة واحدة. تجدها في تبويب المشطوبة.',
      toastDone_two: 'تم شطب فاتورتين. تجدهما في تبويب المشطوبة.',
      toastDone_few: 'تم شطب {{count}} فواتير. تجدها في تبويب المشطوبة.',
      toastDone_many: 'تم شطب {{count}} فاتورةً. تجدها في تبويب المشطوبة.',
      toastDone_other: 'تم شطب {{count}} فاتورة. تجدها في تبويب المشطوبة.',
      asOf: 'حتى تاريخ',
      show: 'عرض',
      customer: 'العميل',
      allCustomers: 'جميع العملاء',
      loadError: 'تعذّر تحميل الفواتير',
      none: 'لا توجد فواتير للشطب',
      noneHint: 'لا توجد فواتير مفتوحة تطابق عوامل التصفية هذه.',
      selectAll: 'تحديد الكل',
      colInvoiceNo: 'رقم الفاتورة',
      colCustomer: 'العميل',
      colInvoiceDate: 'تاريخ الفاتورة',
      colDueDate: 'تاريخ الاستحقاق',
      colDaysOverdue: 'أيام التأخير',
      colTotal: 'الإجمالي',
      colOpenBalance: 'الرصيد المفتوح',
      colVatInBalance: 'الضريبة ضمن الرصيد',
      colVatRelief: 'استرداد الضريبة',
      selectInvoice: 'تحديد {{number}}',
      claim: 'مطالبة',
      notEligible: 'غير مؤهلة',
      writeOffDate: 'تاريخ الشطب',
      badDebtAccount: 'حساب الديون المعدومة',
      invoiceCount_zero: 'لا فواتير',
      invoiceCount_one: 'فاتورة واحدة',
      invoiceCount_two: 'فاتورتان',
      invoiceCount_few: '{{count}} فواتير',
      invoiceCount_many: '{{count}} فاتورةً',
      invoiceCount_other: '{{count}} فاتورة',
      reliefClaimed: 'استرداد الضريبة المطالب به {{amount}}',
      writeOff: 'شطب',
      confirmTitle_zero: 'شطب 0 فاتورة؟',
      confirmTitle_one: 'شطب فاتورة واحدة؟',
      confirmTitle_two: 'شطب فاتورتين؟',
      confirmTitle_few: 'شطب {{count}} فواتير؟',
      confirmTitle_many: 'شطب {{count}} فاتورةً؟',
      confirmTitle_other: 'شطب {{count}} فاتورة؟',
      confirmBody:
        'يُرحَّل مبلغ {{amount}} إلى حساب الديون المعدومة؛ وتُقيَّد الذمم المدينة دائنة وتُسوّى كل فاتورة. يمكنك التراجع عن الشطب لاحقاً.',
      confirmBodyRelief:
        'يُرحَّل مبلغ {{amount}} إلى حساب الديون المعدومة ومبلغ {{relief}} من استرداد الضريبة إلى حساب مراقبة ضريبة القيمة المضافة، مع تسوية في إقرار ضريبة القيمة المضافة المفتوح؛ وتُقيَّد الذمم المدينة دائنة وتُسوّى كل فاتورة. يمكنك التراجع عن الشطب لاحقاً.',
      confirmNotified: 'أكّد أنه تم إبلاغ العميل قبل المطالبة باسترداد الضريبة.',
      notified: 'تم إبلاغ العميل بشطب المبلغ (مطلوب لاسترداد الضريبة)',
      toastUndone: 'تم التراجع عن شطب {{number}}؛ أصبحت الفاتورة مفتوحة مجدداً.',
      historyLoadError: 'تعذّر تحميل عمليات الشطب',
      noneYet: 'لم يُشطب أي شيء بعد',
      noneYetHint: 'تظهر هنا عمليات الشطب التي ترحّلها.',
      colDate: 'التاريخ',
      colInvoice: 'الفاتورة',
      colAmount: 'المبلغ',
      colAccount: 'الحساب',
      colReason: 'السبب',
      colStatus: 'الحالة',
      colAction: 'الإجراء',
      returnPeriod: 'الإقرار {{period}}',
      posted: 'مُرحّل',
      undone: 'تم التراجع',
      undo: 'تراجع',
      undoTitle: 'التراجع عن شطب {{number}}؟',
      undoDescription:
        'يُعكس قيد الشطب بتاريخه، وتعود الفاتورة مفتوحة، وتُحذف أي تسوية لاسترداد الضريبة من إقرار ضريبة القيمة المضافة.',
      undoConfirm: 'التراجع عن الشطب',
    },
    accountantLib: {
      coverPage: 'صفحة الغلاف',
      contents: 'المحتويات',
      notes: 'ملاحظات',
      straightLine: 'القسط الثابت',
      decliningBalance: 'القسط المتناقص',
      statusDisposed: 'مستبعَد',
      statusFullyDepreciated: 'مُهلَك بالكامل',
      statusActive: 'نشط',
      monthYear: '{{month}} {{year}}',
      lifeYearsMonths: '{{years}} و{{months}}',
      months: {
        m1: 'يناير',
        m2: 'فبراير',
        m3: 'مارس',
        m4: 'أبريل',
        m5: 'مايو',
        m6: 'يونيو',
        m7: 'يوليو',
        m8: 'أغسطس',
        m9: 'سبتمبر',
        m10: 'أكتوبر',
        m11: 'نوفمبر',
        m12: 'ديسمبر',
      },
      lifeYears_zero: '{{count}} سنة',
      lifeYears_one: 'سنة واحدة',
      lifeYears_two: 'سنتان',
      lifeYears_few: '{{count}} سنوات',
      lifeYears_many: '{{count}} سنةً',
      lifeYears_other: '{{count}} سنة',
      lifeMonths_zero: '{{count}} شهر',
      lifeMonths_one: 'شهر واحد',
      lifeMonths_two: 'شهران',
      lifeMonths_few: '{{count}} أشهر',
      lifeMonths_many: '{{count}} شهراً',
      lifeMonths_other: '{{count}} شهر',
      errors: {
        upliftNotNumber: 'أدخل نسبة مئوية مثل 10 أو -5.',
        upliftRange: 'أدخل تغييراً بين -100% و1000%.',
        packNameRequired: 'أدخل اسم التقرير الإداري.',
        packDatesRequired: 'اختر التواريخ أو حدّد فترة.',
        packEndBeforeStart: 'تاريخ النهاية يسبق تاريخ البداية.',
        packNoSections: 'أضف قسماً واحداً على الأقل.',
        packTooManySections: 'يمكن أن تضم الحزمة {{max}} قسماً كحد أقصى.',
        assetName: 'أدخل اسم الأصل',
        assetAccount: 'اختر حساب الأصل',
        accumulatedAccount: 'اختر حساب مجمع الإهلاك',
        expenseAccount: 'اختر حساب مصروف الإهلاك',
        acquisitionDate: 'أدخل تاريخ الاقتناء',
        inServiceBeforeAcquired: 'لا يمكن بدء استخدام الأصل قبل تاريخ اقتنائه',
        cost: 'أدخل التكلفة',
        residual: 'يجب أن تكون القيمة المتبقية أقل من التكلفة',
        openingTooHigh: 'لا يمكن أن يتجاوز الإهلاك الافتتاحي التكلفة مطروحاً منها القيمة المتبقية',
        usefulLife: 'أدخل العمر الإنتاجي',
        usefulLifeMax: 'لا يمكن أن يتجاوز العمر الإنتاجي 50 سنة',
        rate: 'أدخل نسبة سنوية من 0.01 إلى 100',
        openingThrough: 'أدخل التاريخ الذي يمتد إليه الإهلاك الافتتاحي',
        openingBeforeService: 'تنتهي الأرصدة الافتتاحية قبل بدء استخدام الأصل',
      },
    },
    assetDrawer: {
      chooseAccount: 'اختر حساباً',
      saved: 'تم الحفظ: {{name}}',
      newTitle: 'أصل ثابت جديد',
      lockedDescription:
        'تم ترحيل الإهلاك، لذا أصبحت التكلفة والحسابات وتاريخ البدء والأرصدة الافتتاحية ثابتة؛ ويُطبَّق العمر الإنتاجي والقيمة المتبقية والطريقة اعتباراً من الشهر التالي.',
      description: 'تكلفة الأصل وموقعه في دليل الحسابات وطريقة إهلاكه.',
      name: 'الاسم',
      serialNo: 'الرقم التسلسلي',
      vendor: 'المورد',
      descriptionLabel: 'الوصف',
      assetAccount: 'حساب الأصل',
      accumulatedAccount: 'حساب مجمع الإهلاك',
      expenseAccount: 'حساب مصروف الإهلاك',
      acquisitionDate: 'تاريخ الاقتناء',
      inServiceFrom: 'بدء الاستخدام',
      inServiceHint: 'يبدأ الإهلاك في هذا الشهر (افتراضياً تاريخ الاقتناء).',
      cost: 'التكلفة ({{currency}})',
      residual: 'القيمة المتبقية ({{currency}})',
      method: 'الطريقة',
      lifeYears: 'العمر الإنتاجي (سنوات)',
      lifeMonths: 'وأشهر',
      annualRate: 'النسبة السنوية (%)',
      rateHint: 'اتركه فارغاً لاستخدام القسط المتناقص المضاعف ({{rate}}% سنوياً).',
      openingLegend: 'الإهلاك المرحَّل قبل هذا السجل',
      openingAccumulated: 'مجمع الإهلاك الافتتاحي ({{currency}})',
      throughEndOf: 'حتى نهاية',
      throughHint: 'الشهر الذي يغطيه.',
    },
    fixedAssetsList: {
      title: 'الأصول الثابتة',
      description:
        'سجل الأصول: التكلفة والإهلاك وصافي القيمة الدفترية. يُرحَّل الإهلاك شهرياً عبر دفتر الأستاذ، ويُسجَّل الربح أو الخسارة عند الاستبعاد.',
      runDepreciation: 'احتساب الإهلاك',
      newAsset: 'أصل جديد',
      loadError: 'تعذّر تحميل السجل',
      emptyTitle: 'لا توجد أصول ثابتة بعد',
      emptyHint: 'أضف المركبات والمعدات والتجهيزات والبرمجيات مع تكلفتها وعمرها الإنتاجي.',
      colAssetNo: 'رقم الأصل',
      colName: 'الاسم',
      colAssetAccount: 'حساب الأصل',
      colInService: 'بدء الاستخدام',
      colMethod: 'الطريقة',
      colCost: 'التكلفة',
      colAccumulated: 'مجمع الإهلاك',
      colNbv: 'صافي القيمة الدفترية',
      colDepreciatedThrough: 'مُهلَك حتى',
      colStatus: 'الحالة',
      methodLife: '{{method}}، {{life}}',
      totalInService: 'إجمالي الأصول المستخدمة',
      runUndone: 'تم التراجع عن إهلاك {{month}}.',
      runsTitle: 'عمليات احتساب الإهلاك',
      colMonth: 'الشهر',
      colRun: 'العملية',
      colAssets: 'الأصول',
      colAmount: 'المبلغ',
      colJournalNo: 'رقم القيد',
      colWhen: 'التوقيت',
      colAction: 'الإجراء',
      triggerScheduled: 'مجدولة',
      triggerSetUp: 'إعداد',
      triggerManual: 'يدوية',
      statusPosted: 'مرحَّلة',
      statusUndone: 'متراجَع عنها',
      undo: 'تراجع',
      undoTitle: 'التراجع عن إهلاك {{month}}؟',
      undoDescription:
        'يُعكس قيد العملية بتاريخه نفسه ويمكن احتساب أشهرها مرة أخرى. ويُرفض ذلك في الشهر المقفل.',
      undoConfirm: 'التراجع عن العملية',
      dialogDescription:
        'يُرحِّل قيداً واحداً لكل شهر بتاريخ آخر يوم فيه، لكل أصل مستحق عليه إهلاك. لا يُحتسب الشهر مرتين؛ كما يُجري السجل هذه العملية كل ليلة.',
      throughEndOf: 'حتى نهاية',
      workingOut: 'جارٍ احتساب المستحق…',
      nothingDue: 'لا يوجد إهلاك مستحق حتى {{month}}.',
      closedMonthTitle: 'هذا الشهر مقفل؛ ستتوقف العملية عنده',
      closedMonth: 'شهر مقفل',
      total: 'الإجمالي {{currency}} {{amount}}',
      posting: 'جارٍ الترحيل…',
      postDepreciation: 'ترحيل الإهلاك',
      postedAll_zero: 'لم يُرحَّل إهلاك لأي شهر.',
      postedAll_one: 'تم ترحيل الإهلاك عن شهر واحد.',
      postedAll_two: 'تم ترحيل الإهلاك عن شهرين.',
      postedAll_few: 'تم ترحيل الإهلاك عن {{count}} أشهر.',
      postedAll_many: 'تم ترحيل الإهلاك عن {{count}} شهراً.',
      postedAll_other: 'تم ترحيل الإهلاك عن {{count}} شهر.',
      postedPartial_zero: 'لم يُرحَّل أي شهر؛ تعذّر ترحيل {{month}}: {{error}}',
      postedPartial_one: 'تم ترحيل شهر واحد؛ تعذّر ترحيل {{month}}: {{error}}',
      postedPartial_two: 'تم ترحيل شهرين؛ تعذّر ترحيل {{month}}: {{error}}',
      postedPartial_few: 'تم ترحيل {{count}} أشهر؛ تعذّر ترحيل {{month}}: {{error}}',
      postedPartial_many: 'تم ترحيل {{count}} شهراً؛ تعذّر ترحيل {{month}}: {{error}}',
      postedPartial_other: 'تم ترحيل {{count}} شهر؛ تعذّر ترحيل {{month}}: {{error}}',
    },
    fixedAssetDetail: {
      scheduleOpening: 'افتتاحي',
      scheduleAdopted: 'قيد مرتبط',
      schedulePosted: 'مرحَّل',
      schedulePlanned: 'مخطط',
      backInRegister: 'عاد {{number}} إلى السجل.',
      deleted: 'تم حذف الأصل.',
      loadError: 'تعذّر تحميل الأصل',
      back: 'الأصول الثابتة',
      rateAYear: '{{rate}}% سنوياً',
      inService: 'بدء الاستخدام {{date}}',
      serial: 'الرقم التسلسلي {{serial}}',
      cost: 'التكلفة',
      accumulated: 'مجمع الإهلاك',
      nbv: 'صافي القيمة الدفترية',
      linkEntries: 'ربط قيود موجودة ({{count}})',
      dispose: 'استبعاد',
      undoDisposal: 'التراجع عن الاستبعاد',
      more: 'المزيد',
      deleteAsset: 'حذف الأصل',
      disposedOn: 'تم الاستبعاد في {{date}}',
      proceeds: 'المتحصلات {{amount}}',
      proceedsInto: 'المتحصلات {{amount}} في {{account}}',
      gainOn: 'ربح {{amount}} في {{account}}',
      lossOn: 'خسارة {{amount}} في {{account}}',
      journalEntry: 'قيد اليومية',
      scheduleTitle: 'جدول الإهلاك',
      colMonth: 'الشهر',
      colDepreciation: 'الإهلاك',
      colAccumulated: 'المجمع',
      colNbv: 'صافي القيمة الدفترية',
      colStatus: 'الحالة',
      throughMonth: 'حتى {{month}}',
      undoDisposalTitle: 'التراجع عن استبعاد {{number}}؟',
      undoDisposalDescription: 'يُعكس قيد الاستبعاد بتاريخه نفسه ويعود الأصل إلى السجل.',
      deleteTitle: 'حذف {{number}}؟',
      deleteDescription: 'لم يُرحَّل أي شيء لهذا الأصل بعد، لذا سيُحذف ببساطة.',
      delete: 'حذف',
      disposalDateRequired: 'أدخل تاريخ الاستبعاد',
      disposalBeforeAcquired: 'تاريخ الاستبعاد يسبق تاريخ اقتناء الأصل',
      disposedToast: 'تم الاستبعاد: {{number}}',
      viewEntry: 'عرض القيد',
      disposeTitle: 'استبعاد {{number}} {{name}}',
      disposeDescription:
        'يُخرج التكلفة ومجمع الإهلاك، ويُدخل المتحصلات، ويُسجّل الربح أو الخسارة. يجب ترحيل الإهلاك حتى الشهر السابق؛ ولا يُحتسب إهلاك في شهر الاستبعاد.',
      disposalDate: 'تاريخ الاستبعاد',
      proceedsLabel: 'المتحصلات ({{currency}})',
      proceedsHint: '0 عند التخريد أو الشطب.',
      proceedsPaidInto: 'إيداع المتحصلات في',
      chooseAccount: 'اختر حساباً',
      gainLossAccount: 'حساب الربح أو الخسارة',
      note: 'ملاحظة',
      posting: 'جارٍ الترحيل…',
      adoptTitle: 'ربط قيود إهلاك موجودة',
      adoptDescription:
        'قيود يومية تُقيّد مسبقاً في الجانب الدائن من {{account}} خلال عمر هذا الأصل. تُعدّ الأشهر المرتبطة مُهلَكة ولا تُرحَّل مرة أخرى.',
      entryCheckbox: '{{date}} · رقم {{number}}',
      selected: 'المحدد {{amount}}',
      linkEntriesButton: 'ربط القيود',
      linkedToast_zero: 'لم يُربط أي قيد بـ {{number}}.',
      linkedToast_one: 'تم ربط قيد واحد بـ {{number}}.',
      linkedToast_two: 'تم ربط قيدين بـ {{number}}.',
      linkedToast_few: 'تم ربط {{count}} قيود بـ {{number}}.',
      linkedToast_many: 'تم ربط {{count}} قيداً بـ {{number}}.',
      linkedToast_other: 'تم ربط {{count}} قيد بـ {{number}}.',
    },
    managementReports: {
      standardName: 'حزمة التقارير الإدارية الشهرية',
      copyName: '{{name}} (نسخة)',
      created: 'تم الإنشاء: {{name}}',
      deleted: 'تم الحذف: {{name}}',
      loadError: 'تعذّر تحميل التقارير الإدارية',
      emptyTitle: 'لا توجد تقارير إدارية بعد',
      emptyHint:
        'يجمع التقرير الإداري الغلاف والتقارير التي تختارها وملاحظاتك في ملف PDF واحد لمجلس الإدارة أو المالك.',
      createStandard: 'إنشاء الحزمة القياسية',
      new: 'تقرير إداري جديد',
      colName: 'الاسم',
      colSections: 'الأقسام',
      colModified: 'آخر تعديل',
      colAction: 'الإجراء',
      exportPdf: 'تصدير PDF',
      moreActions: 'مزيد من الإجراءات لـ {{name}}',
      edit: 'تعديل',
      copy: 'نسخ',
      delete: 'حذف',
      noMatch: 'لا يوجد تقرير إداري مطابق.',
      deleteTitle: 'حذف «{{name}}»؟',
      deleteDescription: 'يُحذف القالب؛ ولا تتأثر الدفاتر ولا التقارير.',
      exportTitle: 'تصدير «{{name}}»',
      exportDescription: 'ملف PDF واحد يضم الغلاف والمحتويات وكل تقارير الفترة وملاحظاتك.',
      reportPeriod: 'فترة التقرير',
      from: 'من',
      to: 'إلى',
      range: '{{from}} – {{to}}',
      asOf: 'في {{date}}',
      preparing: 'جارٍ إعداد ملف PDF…',
      presets: {
        custom: 'مخصص',
        this_week: 'هذا الأسبوع',
        this_week_to_date: 'هذا الأسبوع حتى اليوم',
        this_month: 'هذا الشهر',
        this_month_to_date: 'هذا الشهر حتى اليوم',
        this_quarter: 'هذا الربع',
        this_quarter_to_date: 'هذا الربع حتى اليوم',
        this_year: 'السنة المالية الحالية',
        this_year_to_date: 'السنة المالية الحالية حتى اليوم',
        yesterday: 'أمس',
        last_week: 'الأسبوع الماضي',
        last_month: 'الشهر الماضي',
        last_quarter: 'الربع الماضي',
        last_year: 'السنة المالية السابقة',
        last_7_days: 'آخر 7 أيام',
        last_30_days: 'آخر 30 يوماً',
      },
    },
    packEditor: {
      totalOnly: 'الإجمالي فقط',
      months: 'الأشهر',
      quarters: 'الأرباع',
      saved: 'تم الحفظ: {{name}}',
      back: 'التقارير الإدارية',
      editTitle: 'تعديل التقرير الإداري',
      newTitle: 'تقرير إداري جديد',
      name: 'الاسم',
      reportPeriod: 'فترة التقرير',
      periodHint: 'تُستخدم عند تصدير الحزمة؛ ويمكنك تغييرها حينها.',
      from: 'من',
      to: 'إلى',
      description: 'الوصف',
      sections: 'الأقسام',
      addSection: 'إضافة قسم',
      coverPage: 'صفحة الغلاف',
      contents: 'المحتويات',
      notes: 'ملاحظات',
      reports: 'التقارير',
      comingSoon: 'قريباً؛ غير مُدرج في ملف PDF',
      moveUp: 'نقل لأعلى',
      moveDown: 'نقل لأسفل',
      remove: 'إزالة {{name}}',
      coverTitle: 'عنوان الغلاف',
      coverTitlePlaceholder: 'العنوان (اسم الحزمة)',
      coverSubtitle: 'العنوان الفرعي للغلاف',
      subtitle: 'العنوان الفرعي',
      preparedBy: 'أعدّه',
      sectionTitle: 'عنوان القسم',
      title: 'العنوان',
      columnsBy: 'عرض الأعمدة حسب',
      compare: 'المقارنة',
      noComparison: 'بدون مقارنة',
      previousPeriod: 'الفترة السابقة',
      previousYear: 'السنة السابقة',
      notesTitle: 'عنوان الملاحظات',
      notesText: 'نص الملاحظات',
      notesPlaceholder: 'تعليق على الفترة: ما الذي تغيّر وما الذي يجب متابعته.',
      saveAndExport: 'حفظ وتصدير',
    },
    recurringMode: {
      scheduled: 'مجدولة',
      reminder: 'تذكير',
      unscheduled: 'غير مجدولة',
    },
    recurringKind: {
      journal_entry: 'قيد يومية',
      invoice: 'فاتورة',
      bill: 'فاتورة مورد',
      expense: 'مصروف',
    },
    recurringSchedule: {
      problem: {
        startDate: 'أدخل تاريخ البدء',
        every: 'يجب أن يكون التكرار من 1 إلى 999',
        dayOfWeek: 'اختر يوماً من أيام الأسبوع',
        dayOfMonth: 'اختر يوماً من الشهر',
        month: 'اختر شهراً',
        endAfter: 'يجب أن ينتهي بعد مرة واحدة أو أكثر',
        endDate: 'يجب أن يكون تاريخ الانتهاء في تاريخ البدء أو بعده',
        daysWhole: 'يجب أن يكون عدد الأيام رقماً صحيحاً',
        daysMax: 'يجب ألا تزيد الأيام المسبقة على 365 يوماً',
        endAfterMax: 'يجب أن ينتهي بعد 9999 مرة أو أقل',
      },
      every: {
        daily_one: 'كل يوم',
        daily_other: 'كل {{count}} يوم',
        daily_zero: 'كل {{count}} يوم',
        daily_two: 'كل يومين',
        daily_few: 'كل {{count}} أيام',
        daily_many: 'كل {{count}} يوماً',
        weekly_one: 'كل أسبوع',
        weekly_other: 'كل {{count}} أسبوع',
        weekly_zero: 'كل {{count}} أسبوع',
        weekly_two: 'كل أسبوعين',
        weekly_few: 'كل {{count}} أسابيع',
        weekly_many: 'كل {{count}} أسبوعاً',
        monthly_one: 'كل شهر',
        monthly_other: 'كل {{count}} شهر',
        monthly_zero: 'كل {{count}} شهر',
        monthly_two: 'كل شهرين',
        monthly_few: 'كل {{count}} أشهر',
        monthly_many: 'كل {{count}} شهراً',
        yearly_one: 'كل سنة',
        yearly_other: 'كل {{count}} سنة',
        yearly_zero: 'كل {{count}} سنة',
        yearly_two: 'كل سنتين',
        yearly_few: 'كل {{count}} سنوات',
        yearly_many: 'كل {{count}} سنةً',
      },
      weekly: '{{every}} يوم {{weekday}}',
      monthly: '{{every}} في اليوم {{day}}',
      monthlyLast: '{{every}} في آخر يوم من الشهر',
      yearly: '{{every}} في {{day}} {{month}}',
      yearlyLast: '{{every}} في آخر يوم من {{month}}',
      weekday: {
        d0: 'الأحد',
        d1: 'الاثنين',
        d2: 'الثلاثاء',
        d3: 'الأربعاء',
        d4: 'الخميس',
        d5: 'الجمعة',
        d6: 'السبت',
      },
      month: {
        m1: 'يناير',
        m2: 'فبراير',
        m3: 'مارس',
        m4: 'أبريل',
        m5: 'مايو',
        m6: 'يونيو',
        m7: 'يوليو',
        m8: 'أغسطس',
        m9: 'سبتمبر',
        m10: 'أكتوبر',
        m11: 'نوفمبر',
        m12: 'ديسمبر',
      },
    },
    recurringStrip: {
      ariaLabel: 'التكرار',
      templateName: 'اسم النموذج',
      type: 'النوع',
      mode: 'الوضع',
      createDaysInAdvance: 'الإنشاء مسبقاً (أيام)',
      remindDaysBefore: 'التذكير قبل (أيام)',
      unscheduledHint:
        'لا تُنشأ النماذج غير المجدولة تلقائياً. استخدمها من قائمة المعاملات المتكررة.',
      interval: 'الفترة',
      daily: 'يومي',
      weekly: 'أسبوعي',
      monthly: 'شهري',
      yearly: 'سنوي',
      on: 'في يوم',
      month: 'الشهر',
      day: 'اليوم',
      last: 'آخر يوم',
      everyDays: 'كل (أيام)',
      everyWeeks: 'كل (أسابيع)',
      everyMonths: 'كل (أشهر)',
      everyYears: 'كل (سنوات)',
      startDate: 'تاريخ البدء',
      end: 'الانتهاء',
      endNone: 'بلا انتهاء',
      endAfter: 'بعد عدد مرات',
      endBy: 'في تاريخ',
      occurrences: 'عدد المرات',
      endDate: 'تاريخ الانتهاء',
      firstOn: 'أول مرة في {{date}}',
      nextOn: 'المرة التالية في {{date}}',
      pastDates: 'التواريخ السابقة لليوم تُنشأ في التشغيل التالي.',
    },
    recurringList: {
      title: 'المعاملات المتكررة',
      description:
        'نماذج تُنشئ المعاملات وفق جدول، أو تذكّرك عند استحقاقها، أو تنتظر حتى تستخدمها.',
      selectClientHint: 'اختر عميلاً لعرض نماذجه.',
      paused: 'تم إيقاف "{{name}}" مؤقتاً.',
      resumed: 'تم استئناف "{{name}}".',
      resumedNext: 'تم استئناف "{{name}}"؛ المرة التالية في {{date}}.',
      deleted: 'تم حذف "{{name}}".',
      draftCreated: {
        invoice: 'أُنشئت مسودة فاتورة من "{{name}}".',
        bill: 'أُنشئت مسودة فاتورة مورد من "{{name}}".',
        expense: 'أُنشئت مسودة مصروف من "{{name}}".',
      },
      newTemplate: 'نموذج جديد',
      search: 'البحث باسم النموذج',
      couldNotLoad: 'تعذّر تحميل النماذج',
      emptyTitle: 'لا توجد معاملات متكررة بعد',
      emptyHint:
        'أعدّ الإيجار والأتعاب الدورية والاشتراكات والمستحقات مرة واحدة، ودعها تتكرر في كل فترة.',
      col: {
        templateName: 'اسم النموذج',
        type: 'النوع',
        interval: 'الفترة',
        previousDate: 'التاريخ السابق',
        nextDate: 'التاريخ التالي',
        contact: 'العميل/المورد',
        amount: 'المبلغ',
        mode: 'الوضع',
        action: 'الإجراء',
      },
      badgePaused: 'متوقف مؤقتاً',
      badgeDue: 'مستحق',
      createFailed: {
        journal_entry: 'تعذّر إنشاء القيد {{date}}: {{error}}',
        invoice: 'تعذّر إنشاء الفاتورة {{date}}: {{error}}',
        bill: 'تعذّر إنشاء فاتورة المورد {{date}}: {{error}}',
        expense: 'تعذّر إنشاء المصروف {{date}}: {{error}}',
      },
      createFailedOther: 'تعذّر إنشاء {{kind}} {{date}}: {{error}}',
      use: 'استخدام',
      duplicate: 'نسخ',
      resume: 'استئناف',
      pause: 'إيقاف مؤقت',
      delete: 'حذف',
      moreActions: 'مزيد من الإجراءات لـ {{name}}',
      deleteTitle: 'حذف «{{name}}»؟',
      deleteDescription:
        'يتوقف النموذج عن إنشاء المعاملات، وتبقى المعاملات التي أنشأها في الدفاتر.',
      deleting: 'جارٍ الحذف…',
    },
    recurringTemplate: {
      couldNotOpen: 'تعذّر فتح النموذج',
    },
    budgetList: {
      title: 'الموازنات',
      description: 'خطّط للإيرادات والمصروفات حسب الحساب والشهر، ثم قارنها بالأرقام الفعلية.',
      newBudget: 'موازنة جديدة',
      deleted: 'تم حذف الموازنة "{{name}}".',
      created: 'تم إنشاء الموازنة "{{name}}".',
      couldNotLoad: 'تعذّر تحميل الموازنات',
      emptyTitle: 'لا توجد موازنات بعد',
      emptyHint: 'ابدأ من الأرقام الفعلية للسنة الماضية مع نسبة زيادة، أو من سنة فارغة.',
      col: {
        name: 'الاسم',
        financialYear: 'السنة المالية',
        income: 'الإيرادات',
        expenses: 'المصروفات',
        netIncome: 'صافي الدخل',
        lastModified: 'آخر تعديل',
        action: 'الإجراء',
      },
      view: 'عرض',
      delete: 'حذف',
      moreActions: 'مزيد من الإجراءات لـ {{name}}',
      deleteTitle: 'حذف الموازنة «{{name}}»؟',
      deleteDescription: 'تُحذف مبالغها، ولا تتأثر الدفاتر ولا الأرقام الفعلية.',
      yearLabel: 'السنة المالية {{year}}',
      defaultName: 'موازنة {{fy}}',
      dialogTitle: 'موازنة جديدة',
      dialogDescription: 'تغطي الموازنة سنة مالية واحدة، حسب حسابات الأرباح والخسائر والشهر.',
      name: 'الاسم',
      financialYear: 'السنة المالية',
      startFrom: 'البدء من',
      fromActuals: 'الأرقام الفعلية لسنة مالية',
      fromBudget: 'موازنة أخرى',
      fromBlank: 'فارغة',
      actualsOf: 'الأرقام الفعلية للسنة',
      budget: 'الموازنة',
      chooseBudget: 'اختر موازنة',
      changeBy: 'التغيير بنسبة (%)',
      changeByHint: 'القيمة 10 تضيف 10% إلى كل مبلغ، و−5 تخصم 5%.',
      creating: 'جارٍ الإنشاء…',
      create: 'إنشاء الموازنة',
    },
    budgetEditor: {
      couldNotLoad: 'تعذّر تحميل الموازنة',
      budgetName: 'اسم الموازنة',
      nameRequired: 'أدخل اسم الموازنة.',
      byMonth: '{{fy}} · حسب الشهر',
      showEmpty: 'إظهار الحسابات التي ليس لها مبالغ',
      vsActuals: 'الموازنة مقابل الفعلي',
      revert: 'تراجع',
      saved: 'تم حفظ الموازنة "{{name}}".',
      account: 'الحساب',
      total: 'الإجمالي',
      rowActions: 'إجراءات الصف لـ {{name}}',
      copyAcross: 'نسخ الشهر الأول إلى بقية الأشهر',
      spread: 'توزيع الإجمالي بالتساوي',
      clearRow: 'مسح الصف',
      sectionTotal: 'إجمالي {{section}}',
      netIncome: 'صافي الدخل',
      section: {
        income: 'الإيرادات',
        cost_of_sales: 'تكلفة المبيعات',
        expenses: 'المصروفات',
        other_income: 'إيرادات أخرى',
        other_expense: 'مصروفات أخرى',
      },
    },
    practiceClients: {
      title: 'العملاء',
      description: 'كل العملاء الذين يمكنك فتح دفاترهم.',
      addClient: 'إضافة عميل',
      search: 'البحث بالاسم أو الرقم الضريبي',
      searchLabel: 'البحث في العملاء',
      couldNotLoad: 'تعذّر تحميل العملاء',
      emptyTitle: 'لا يوجد عملاء بعد',
      noMatch: 'لا يوجد عملاء يطابقون بحثك',
      emptyHint: 'أضف العميل الأول لتبدأ مسك دفاتره.',
      col: {
        client: 'العميل',
        taxNumber: 'الرقم الضريبي',
        taxPeriod: 'الفترة الضريبية',
        bankItems: 'بنود بنكية للمراجعة',
        unreconciled: 'حسابات غير مسوّاة',
        lastLocked: 'آخر فترة مقفلة',
        accountants: 'المحاسبون',
        action: 'الإجراء',
      },
      open: 'مفتوح',
      openBooks: 'فتح الدفاتر',
      statusError: 'تعذّرت قراءة حالة هذا العميل',
      notRegisteredVat: 'غير مسجل في ضريبة القيمة المضافة',
      notRegisteredGst: 'غير مسجل في GST',
      due: '{{label}} · تستحق في {{date}}',
      filed: {
        monthly: 'شهري · مُقدَّم',
        quarterly: 'ربع سنوي · مُقدَّم',
      },
      filedOther: '{{period}} · مُقدَّم',
      chip: {
        open: 'مفتوحة',
        dueToday: 'تستحق اليوم',
        overdue: 'متأخرة',
        filed: 'مُقدَّمة',
        paid: 'مدفوعة',
        dueIn_one: 'تستحق خلال يوم واحد',
        dueIn_other: 'تستحق خلال {{count}} يوم',
        dueIn_zero: 'تستحق خلال {{count}} يوم',
        dueIn_two: 'تستحق خلال يومين',
        dueIn_few: 'تستحق خلال {{count}} أيام',
        dueIn_many: 'تستحق خلال {{count}} يوماً',
      },
      added: 'تمت إضافة {{name}}.',
      drawerTitle: 'إضافة عميل',
      drawerDescription:
        'يُعدّ الدفاتر: السنوات المالية، ونسب الضريبة لبلد العميل، ودليل الحسابات من النموذج.',
      country: 'البلد',
      countryHint: 'يحدد العملة الأساسية والضريبة والسنة المالية والرقم الضريبي.',
      india: 'الهند',
      uae: 'الإمارات العربية المتحدة',
      clientName: 'اسم العميل',
      legalName: 'الاسم القانوني',
      legalNameHintIndia:
        'كما هو مسجل (شهادة التأسيس أو تسجيل GST). يُستخدم اسم العميل إن تُرك فارغاً.',
      legalNameHintUae: 'كما هو مسجل (الرخصة التجارية). يُستخدم اسم العميل إن تُرك فارغاً.',
      state: 'الولاية',
      emirate: 'الإمارة',
      gstin: 'GSTIN',
      gstinHint: '15 حرفاً؛ اتركه فارغاً إن لم يكن العميل مسجلاً في GST.',
      trn: 'رقم التسجيل الضريبي',
      trnHint: '15 رقماً؛ اتركه فارغاً إن لم يكن العميل مسجلاً في ضريبة القيمة المضافة.',
      homeCurrency: 'العملة الأساسية',
      currency: {
        IN: 'INR – روبية هندية',
        AE: 'AED – درهم إماراتي',
      },
      firstMonth: 'أول شهر في السنة المالية',
      returnPeriodVat: 'فترة إقرار ضريبة القيمة المضافة',
      returnPeriodGst: 'فترة إقرار GST',
      quarterly: 'ربع سنوي',
      monthly: 'شهري',
      chartTemplate: 'نموذج دليل الحسابات',
      template: {
        trading: 'تجارة (بضائع)',
        services: 'خدمات',
        free_zone: 'شركة منطقة حرة',
        india_trading: 'تجارة (بضائع)، الهند',
        india_services: 'خدمات، الهند',
      },
      error: {
        name: 'أدخل اسم العميل.',
        trn: 'يتكون رقم التسجيل الضريبي من 15 رقماً.',
        gstinState: 'رقم GSTIN مسجل في {{state}}.',
      },
      emirates: {
        abuDhabi: 'أبوظبي',
        dubai: 'دبي',
        sharjah: 'الشارقة',
        ajman: 'عجمان',
        ummAlQuwain: 'أم القيوين',
        rasAlKhaimah: 'رأس الخيمة',
        fujairah: 'الفجيرة',
      },
      countOf_one: '{{shown}} من عميل واحد',
      countOf_other: '{{shown}} من {{count}} عميل',
      countOf_zero: '{{shown}} من {{count}} عميل',
      countOf_two: '{{shown}} من عميلين',
      countOf_few: '{{shown}} من {{count}} عملاء',
      countOf_many: '{{shown}} من {{count}} عميلاً',
      overdue_one: 'إقرار متأخر واحد',
      overdue_other: '{{count}} إقرار متأخر',
      overdue_zero: '{{count}} إقرار متأخر',
      overdue_two: 'إقراران متأخران',
      overdue_few: '{{count}} إقرارات متأخرة',
      overdue_many: '{{count}} إقراراً متأخراً',
    },
    shortcuts: {
      title: 'اختصارات لوحة المفاتيح',
      description: 'على macOS استخدم Control+Option.',
      anywhere: 'في أي مكان',
      dateFields: 'حقول التاريخ',
      amountFields: 'حقول المبالغ',
      comingSoon: 'قريباً',
      amountHint:
        'اكتب عملية حسابية مثل <c1>1200*1.05</c1> أو <c2>(450+380)/2</c2>، وتُحسب النتيجة عند مغادرة الحقل أو الضغط على <k>=</k>.',
      action: {
        chartOfAccounts: 'دليل الحسابات',
        search: 'البحث',
        searchSlash: 'البحث (عندما لا تكتب في حقل)',
        showShortcuts: 'عرض الاختصارات',
        saveClose: 'حفظ وإغلاق (في نموذج)',
        saveNew: 'حفظ وجديد (في نموذج)',
        exitForm: 'الخروج من النموذج (داخل نموذج)',
        newInvoice: 'فاتورة جديدة',
        newQuote: 'عرض سعر جديد',
        receivePayment: 'استلام دفعة',
        vendorPayment: 'دفعة لمورد',
        customers: 'قائمة العملاء',
        vendors: 'قائمة الموردين',
        lists: 'القوائم (ترس الإعدادات)',
        help: 'المساعدة',
        send: 'إرسال، أو اعتماد وإرسال (في نموذج)',
      },
      date: {
        today: 'اليوم',
        nextPrevDay: 'اليوم التالي / السابق',
        week: 'أول / آخر يوم في الأسبوع',
        month: 'أول / آخر يوم في الشهر',
        year: 'أول / آخر يوم في السنة',
      },
    },
  },
};
